/**
 * DeliverScreen v120.5 — Unified Deliver Workflow
 * 4th "D" in Orella workflow — Chat Dialogue Pattern
 * 
 * v120.5 changes:
 *   - Encounter loop completion: pre-visit report wired to encountersAPI
 *   - Phase transitions: deliver_active → deliver_submitted → diagnose_pending
 *   - API auto-sync ensures encounters table stays current
 *
 * v120.4 changes:
 *   - Extracted modals to separate files with chat dialogue UX
 *   - FollowUpReportModal: AI last visit summary + voice/text
 *   - SymptomCheckModal: gated question progression with detail boxes
 *   - MedicationReviewModal: per-med adherence with amber info pills
 *   - FurtherTestingScreen: CompactHeader + chat dialogue cards
 *   - InstructionsScreen: CompactHeader + chat dialogue checklist
 *
 * Structure:
 *   1. Greeting bubble → triggers sequential cascade
 *   2. Doctor mini card
 *   3. Order-based care cards (labs, imaging, care instructions) — ungated
 *   4. State-specific card (scheduling, appointment, observation, complete, referral)
 *   5. Progress bar
 *   6. Follow-Up Report (voice recording) — GATED
 *   7. Symptom Check-In — GATED
 *   8. Medication Review (if meds) — GATED
 *   9. Submit to doctor
 * 
 * 6 Follow-up plan states:
 *   follow-up, scheduled, all-done, observation, complete, referral
 */

import { useState, useEffect, useMemo, useRef } from 'react'
import { useNavigate, useLocation, Navigate } from 'react-router-dom'
import { Mic, Send, Check, CheckCircle, ChevronRight, Calendar, Clock, Building, Video, MapPin, Phone, Pill, FileText, Eye, Monitor, AlertCircle, Activity, ListChecks, MessageSquare, Scissors, Syringe, FlaskConical, RefreshCw, ExternalLink, User, Camera, X } from 'lucide-react'
import { splitClinicalList } from '../../utils/clinicalListSplit'
import { asText, asFirst, asTextList } from '../../utils/asText' // PROD-1156: a case-plan value is drawn as text, never as an object
import { getPatientName } from '../../utils/profileFormat'
import { legacyIds, dayKey } from '../../utils/careTicks'
import { createPortal } from 'react-dom'
import { useCaseStore, useProfileStore } from '../../stores'
import { openReferralLetter } from '../../utils/referralLetterPDF'
import { currentEncounterNumber, currentEncounterId } from '../../utils/caseEncounters'
import { caseDoctor } from '../../utils/caseDoctor'
import BottomSheet from '../../components/BottomSheet'
import JoinVideoVisit from '../../components/JoinVideoVisit'
import { videoJoinTarget, joinCheckInPath } from '../../utils/videoVisit'
import { OrellaSpinner } from '../../components/OrellaLoader'
import DoctorProfileScreen from '../doctors/DoctorProfileScreen'
import { useI18n } from '../../i18n'
import { formatProviderSpecialty } from '../../utils/specialtyResolver'
import { ChatKeyframes, CompactHeader, OrellaChatBubble, SmallOrellaBubble, AnimatedCard } from '../../components/OrellaChatHeader'
import orellaOIcon from '../../assets/logos/orella_o_icon.png'
import SubmitOverlay, { runOverlaySequence } from '../../components/SubmitOverlay'
import { formatDate, formatDateLong, formatAppointmentTime, parseDate, formatCalendarDay } from '../../utils/dateUtils'
import { formatAddress } from '../../utils/formatAddress'
import { deliverNeedsOrellaMD } from '../../utils/caseGating'
import { encountersAPI, casesAPI, aiAPI, doctorsAPI } from '../../services/api'
import { readServerAppointmentRequest } from '../../utils/appointmentRequestOwner'
import { bookableSlots, datesOf, timesForDate, toHHMM, appointmentErrorKeyFor, concernSentMessageKeyFor, reasonAllowsSendWithoutSlot, isSlotConflict, concernRecordedWithoutAppointment, slotPlace, slotPlaceNames } from '../../utils/bookableSlots'
import SlotPlaceLine from '../../components/SlotPlaceLine'
import NoSlotsCard from '../../components/NoSlotsCard'
import useActiveCarePlan from '../../hooks/useActiveCarePlan'
import { caseHasSignedVisit, planFollowUp, signedOrdersPlaced } from '../../utils/signedPlan' // PROD-1079: no draft plan reaches the patient
import { useAgent } from '../../components/AgentContext'
import AppointmentCalendar from '../../components/AppointmentCalendar'
import ScheduleFollowupScreen from '../diagnose/ScheduleFollowupScreen'

// v120.4: Extracted modals
import FollowUpReportModal from './FollowUpReportModal'
import { followUpScaleOrders } from '../../utils/followUpScales' // s128: board D
import scalesLib from '../../data/scales.json'
import questionnairesData from '../../data/questionnaires.json'
import SymptomCheckModal from './SymptomCheckModal'
// CHECKUP-MODAL-REUSE-1 (session 91): one owner for "is this case under observation".
// This file used to answer it in six places with four predicates; see utils/caseObservation.js.
import { isObservationCase, bridgeTypeFor } from '../../utils/caseObservation'
import MedicationReviewModal from './MedicationReviewModal'
// v173.0.0: Unified follow-up questions (inline, replaces separate symptom/med modals when AI succeeds)
import FollowUpQuestionsSection, { categorizeFollowUpAnswers } from './FollowUpQuestionsSection'
import { DIALOGUE_BUBBLE, DIALOGUE_PAD } from '../../components/agent/dialogueBubble'
import { caseBack } from '../../utils/caseBack' // PROD-654: back = the previous case screen
import { readAvsText, unwrapAvs } from '../../utils/avsText' // PROD-134 gamma: the one AVS reader
import { asPendingRequest } from '../../utils/pendingReschedule' // PROD-921
import { isOutsideAppointment } from '../../utils/outsideAppointment' // PROD-1160
import { clinicianFromText, clinicianName, withCaseClinician } from '../../utils/clinicianName.js'

const NAVY = '#15354F'
const GREEN = '#66D170'
const GREEN_DARK = '#16A34A'
const GREEN_LIGHT = '#D5EFD3'
const BLUE = '#46A8F9'
const ICE_BLUE = '#E9F8FF'
const LIGHT_BLUE = '#A7D9FC'

// Scan icon (not in lucide-react@0.263.1)
const Scan = ({ size = 24, className = '', style = {} }) => (
  <svg className={className} style={style} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 7V5a2 2 0 0 1 2-2h2"/><path d="M17 3h2a2 2 0 0 1 2 2v2"/><path d="M21 17v2a2 2 0 0 1-2 2h-2"/><path d="M7 21H5a2 2 0 0 1-2-2v-2"/><rect x="7" y="7" width="10" height="10" rx="1"/>
  </svg>
)

// Package icon — v221.1.2, used for DME (Equipment & Supplies).
// Defined inline to match the codebase's pattern for icons not in the pinned lucide-react version.
const Package = ({ size = 24, className = '', style = {} }) => (
  <svg className={className} style={style} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M16.5 9.4 7.55 4.24"/>
    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
    <polyline points="3.27 6.96 12 12.01 20.73 6.96"/>
    <line x1="12" y1="22.08" x2="12" y2="12"/>
  </svg>
)

// Heart icon
const Heart = ({ size = 24, className = '', style = {} }) => (
  <svg className={className} style={style} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
  </svg>
)

// ★★★ v242.116.0 · CARECARD-MDPANEL-1 — THE DUE PILL IS THE ONLY THING ON A CARE
//   CARD THAT WEARS A COLOUR. Ported value-for-value from the MD panel's
//   DeliverWorkflowSection > DUE_DATE_OPTIONS (v243.9.0, TASKCARD-PALETTE-1), because
//   Dr. Rami's ask was literally "make the care cards look more like MD panel colour
//   and design wise" — so the card the physician builds and the card the patient
//   receives are now drawn from ONE set of values.
//   ★★ THE CATEGORY IS ALREADY SAID TWICE — by the glyph and by the title. The ten
//     ICON_COLORS tint pairs deleted here said it a third time, in colour, on a screen
//     where amber then had nothing left to mean. A hue that carries no decision is not
//     emphasis; it is something the reader has to work out is meaningless.
//   ★ These are the BRAND tokens, not Tailwind's palette: bg-green-100 is not #D5EFD3,
//     and two greens one shade apart on one screen read as two different states.
const DUE_PILL = {
  'by-followup':         'bg-[#D5EFD3] text-[#16A34A] border-[#BBE9BF]',
  // ★★★ THESE TWO WERE THE ONLY GREY LEFT ON A CARE CARD. v243.81.0 ruled "NO GREY
  //   BACKGROUND anywhere in the app" and remapped the slate 50-300 scale to blue tints —
  //   but these are ARBITRARY values (`bg-[#F1F5F9]`), which bypass the scale entirely, so
  //   they survived the ruling in the one place a patient sees most. Moved to the ice pair
  //   the brand already owns and that `custom`/`scheduled` already use.
  'under-consideration': 'bg-[#E9F8FF] text-[#15354F] border-[#A7D9FC]',
  'asap':                'bg-[#FFFBEB] text-[#B45309] border-[#FDE68A]',
  'custom':              'bg-[#E9F8FF] text-[#15354F] border-[#A7D9FC]',
  'done':                'bg-[#D5EFD3] text-[#16A34A] border-[#BBE9BF]',
  'required':            'bg-[#FFFBEB] text-[#B45309] border-[#FDE68A]',
  'scheduled':           'bg-[#E9F8FF] text-[#15354F] border-[#A7D9FC]',
  'neutral':             'bg-[#E9F8FF] text-[#15354F] border-[#A7D9FC]',
}


const TASK_ICONS = {
  careOrders: Activity,
  medication: Pill,
  labs: FlaskConical,
  imaging: Scan,
  referrals: Send,
  diagnosticTests: ListChecks,    // v221.1.2: new bucket
  dme: Package,                   // v221.1.2: new bucket — Lucide Package per design spec
  additionalOrders: ListChecks,   // legacy back-compat
  procedures: Syringe,
  customOrders: FileText,
  other: AlertCircle,
}

// ============================================
// CARE CARD — unified tappable task card
// ============================================
// ★★★ ONE CARD LANGUAGE (D332, and the criteria mockup he settled on 2026-08-27).
//   Observation and Care Complete were two more hand-rolled card families in this file,
//   each with its own radius, border, tile and footer — three vocabularies on one screen
//   for what is, to a patient, one kind of object. They are instances of this shell now.
//   THE NEW SLOTS, all optional, so every existing caller is untouched:
//     tone   'care' (default) · 'observation' · 'complete' — identity, nothing more
//     strip  { label, percent } — the window countdown, drawn once, from the ELAPSED end
//     plate  { heading, items } — the escalation / come-back-if block
//     action a footer affordance, where the progress counter sits on a care card
//   ★★ THE PLATE IS THE ONLY EMPHASIS ON THE CARD, and it is ice inside a NAVY BORDER —
//     not red. Red is `md.danger`, a legacy token that appears nowhere else in this app
//     except destructive confirmations, and these criteria are read most days for a
//     fortnight. A colour that shouts is spent once it becomes the background of daily
//     life. Red stays for the ACT (Report a change), where irreversible is what it means.
//   ★★ AMBER IS GONE ENTIRELY. It is not a brand token, `FOLLOWUP_AMBER` was retired in
//     v221.0.0, and in DUE_PILL it still means `asap`/`required` — the opposite of what an
//     observation asks of a patient, on the same screen.
function CareCard({ icon: Icon, title, subtitle, items, checkedIds, pill, pillLabel, status,
                    meta, facility, onClick, tone = 'care', strip, plate, action }) {
  const isComplete = status === 'complete' || status === 'completed'
  const list = Array.isArray(items) ? items : []
  const checked = new Set(checkedIds || [])
  const doneCount = list.filter(it => checked.has(it.id)).length
  const pillTone = DUE_PILL[isComplete ? 'done' : pill] || DUE_PILL.neutral
  const tileBg = tone === 'complete' || isComplete ? '#66D170'
    : tone === 'observation' ? '#15354F' : '#46A8F9'
  const borderCol = tone === 'complete' ? '#BBE9BF' : isComplete ? '#E2E8F0' : '#15354F'

  return (
    <button type="button" onClick={onClick} disabled={!onClick}
      style={{ borderColor: borderCol }}
      className={`w-full text-left rounded-2xl border bg-white mb-2.5 p-[13px] pb-[11px] transition-all ${onClick ? 'cursor-pointer active:bg-slate-50' : 'cursor-default'}`}>
      {/* ── header: the tile, the title, and the one coloured thing ── */}
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
             style={{ backgroundColor: isComplete && tone === 'care' ? '#D5EFD3' : tileBg }}>
          {isComplete && tone === 'care'
            ? <Check size={16} style={{ color: '#16A34A' }} strokeWidth={2.5} />
            : <Icon size={16} style={{ color: '#FFFFFF' }} />}
        </div>
        <div className="min-w-0 flex-1">
          <p className={`text-[14.5px] font-bold leading-tight ${isComplete ? 'text-slate-500' : 'text-[#15354F]'}`}>{title}</p>
          {subtitle && <p className="text-[11px] text-slate-500 mt-0.5">{subtitle}</p>}
        </div>
        {pillLabel && (
          <span className={`flex-none text-[9.5px] font-extrabold uppercase tracking-wide px-[11px] py-1 rounded-full border whitespace-nowrap ${pillTone}`}>
            {pillLabel}
          </span>
        )}
      </div>

      {/* ── the plan, open, and READ-ONLY ──────────────────────────────────────
          ★★★ v242.119.0 · CARECARD-NOT-A-CHECKLIST-1 — Dr. Rami: "I dont want the
            cards to be checklists themselves. They are clickable for the info and
            actions." The tick rings are gone and the item rows are no longer
            buttons; THE CARD IS ONE TAP TARGET again, and everything you can DO
            with an order lives on the screen it opens.
          ★★★ THIS IS THE MD PANEL'S OWN RULE, ARRIVING HERE ONE VERSION LATE:
            OrderTaskCard draws a 6px dot rather than a box precisely so that a mark
            it cannot write does not invite a click. v242.116.0 read that as "the
            patient CAN write, so draw the control" — but a ring on every row makes
            the card look like the place the work happens, and the work happens one
            screen in, where the instructions and the education sit.
          ★★ THE STATE IS STILL CARRIED, STILL READ BACK: an item ticked on
            InstructionsScreen shows as a blue check with no box around it, struck
            through, and the counter below reflects it. Nothing here is clickable on
            its own and nothing here looks like it is. ── */}
      {/* ── the window ──
          ★ Its ground is WHITE on a light-blue border, not ice — the plate below owns ice,
            and two ice fills on one card make neither of them mean anything.
          ★★ THE OLD BAR WAS NOT DRAWN FROM THE WRONG END, which is what it looked like:
            the percentage was always elapsed-based. It read ~93% full on day 6 of a ONE
            WEEK window because the start date fell back to `end - 14 days` REGARDLESS OF
            THE INTERVAL THE DOCTOR PICKED. The fallback now uses the real interval. */}
      {strip && (
        <div className="flex items-center gap-2.5 mt-[11px] rounded-[10px] border px-2.5 py-2"
             style={{ borderColor: '#A7D9FC', backgroundColor: '#FFFFFF' }}>
          <span className="flex-none text-[11.5px] font-bold" style={{ color: '#15354F' }}>{strip.label}</span>
          <span className="flex-1 h-[6px] rounded-full overflow-hidden block border"
                style={{ backgroundColor: '#E9F8FF', borderColor: '#A7D9FC' }}>
            <span className="block h-full" style={{ width: `${Math.max(0, Math.min(100, strip.percent || 0))}%`, backgroundColor: '#46A8F9' }} />
          </span>
        </div>
      )}

      {list.length > 0 && (
        <ul className="mt-2.5 list-none p-0 m-0">
          {list.map((item) => {
            const done = checked.has(item.id)
            return (
              <li key={item.id} className="flex items-start gap-2.5 py-[7px] text-[12.5px] leading-normal">
                <span className="flex-none mt-[6px] w-[14px] flex items-center justify-center">
                  {done
                    ? <Check size={14} style={{ color: '#46A8F9', marginTop: '-3px' }} strokeWidth={2.5} />
                    : <span className="w-[6px] h-[6px] rounded-full bg-[#46A8F9] block" />}
                </span>
                <span className={`min-w-0 break-words ${done ? 'text-slate-400 line-through' : 'text-slate-700'}`}>{item.text}</span>
              </li>
            )
          })}
        </ul>
      )}

      {/* ── the plate: the one thing on this card that carries a decision ── */}
      {plate && Array.isArray(plate.items) && plate.items.length > 0 && (
        <div className="mt-[13px] rounded-[11px] px-3 py-[11px] border"
             style={{ backgroundColor: '#E9F8FF', borderColor: '#15354F' }}>
          <p className="text-[9.5px] font-extrabold uppercase tracking-[0.1em] m-0 mb-1.5"
             style={{ color: '#15354F' }}>{plate.heading}</p>
          <ul className="list-none p-0 m-0">
            {plate.items.map((line, i) => (
              <li key={i} className="flex items-start gap-2 py-[3px] text-[12.5px] leading-normal"
                  style={{ color: '#15354F' }}>
                <span className="flex-none mt-[7px] w-[5px] h-[5px] rounded-full block"
                      style={{ backgroundColor: '#15354F' }} />
                <span className="min-w-0 break-words">{line}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* ── footer: where it happens, and the progress read back ── */}
      {(list.length > 0 || facility?.name || meta || action) && (
        <div className="flex items-center gap-2 mt-[11px] pt-2.5 border-t border-slate-100">
          {facility?.name ? (
            <span className="flex items-center gap-1.5 text-[11px] text-slate-500 min-w-0">
              <MapPin size={12} style={{ color: '#46A8F9' }} className="flex-shrink-0" />
              <span className="truncate">{facility.name}</span>
            </span>
          ) : meta ? (
            <span className="text-[11px] text-slate-500 min-w-0 truncate">{meta}</span>
          ) : null}
          {list.length > 0 ? (
            <span className="ml-auto flex items-center gap-1.5 text-[10.5px] font-bold text-slate-400 flex-none">
              <span className="w-[54px] h-[5px] rounded-[3px] bg-slate-100 overflow-hidden block">
                <span className="block h-full bg-[#46A8F9]" style={{ width: `${Math.round((doneCount / list.length) * 100)}%` }} />
              </span>
              {doneCount} / {list.length}
            </span>
          ) : action ? (
            <span className="ml-auto flex-none text-[11.5px] font-bold" style={{ color: '#46A8F9' }}>{action}</span>
          ) : (
            onClick && <ChevronRight size={16} className="text-slate-300 ml-auto flex-shrink-0" />
          )}
        </div>
      )}
    </button>
  )
}

// ============================================
// APPOINTMENT SUMMARY CARD (green accent)
// ============================================
function AppointmentSummaryCard({ appointment, visitType, onReschedule, onJoinCheckIn = null }) {
  const { t } = useI18n()
  if (!appointment) return null
  const isVirtual = visitType === 'virtual' || visitType === 'telehealth' || visitType === 'video' ||
                    appointment.visitType === 'virtual' || appointment.visitType === 'telehealth'
  // MEET PA2: appointment.join = videoJoinTarget(<row>) (link, id, provider, decision), opened only
  //   through JoinVideoVisit (the recording question first).
  const joinTarget = appointment.join || null
  // ★ PROD-921: a pending_reschedule row reads as 'pending' (asPendingRequest) — before, it fell to "Confirmed".
  const apptStatus = asPendingRequest(appointment.status)
  const isPending = apptStatus === 'rescheduled' || apptStatus === 'pending' || apptStatus === 'requested'
  // ★ PROD-1011 (Alex Q67): a pending request / held reschedule never shows Join, even with a link.
  const showJoin = isVirtual && !!joinTarget && !isPending
  // ★★★ v242.116.0 · CARECARD-MDPANEL-1 — the appointment joins the card family.
  //   The 3px green rule and the green border are gone; green survives where it MEANS
  //   something — the CONFIRMED pill — because green is the follow-up identity colour
  //   (D122, D200) and a booked follow-up is exactly what it identifies. A pending
  //   reschedule takes the same amber the ASAP due pill takes, and for the same reason:
  //   it is the one thing on the screen waiting on somebody.
  return (
    <div className="rounded-2xl border border-[#15354F] bg-white mb-2.5 p-[13px] pb-[11px]">
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 bg-[#46A8F9]">
          <Calendar size={16} style={{ color: '#FFFFFF' }} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[14.5px] font-bold leading-tight text-[#15354F]">
            {isPending ? t('deliver.rescheduleRequested') : t('deliver.followUpScheduled')}
          </p>
          <p className="text-[11px] text-slate-500 mt-0.5">
            {isVirtual ? t('deliver.virtualVisit') : t('deliver.inPersonVisit')}
            {appointment.doctorName ? ` \u00b7 ${appointment.doctorName}` : ''}
          </p>
        </div>
        <span className={`flex-none text-[9.5px] font-extrabold uppercase tracking-wide px-[11px] py-1 rounded-full border whitespace-nowrap ${isPending ? DUE_PILL.asap : DUE_PILL.done}`}>
          {isPending ? 'Pending' : 'Confirmed'}
        </span>
      </div>
      <div>
        
        {/* ★ v243.81.0 "no grey background anywhere" — this was an arbitrary #F1F5F9 and
            so bypassed the remapped slate scale, same as the two DUE_PILL entries above. */}
        <div className="bg-[#E9F8FF] rounded-xl p-3 mt-2.5 mb-2.5 space-y-1.5">
          <div className="flex items-center gap-2 text-[13px] font-semibold" style={{ color: NAVY }}>
            <Calendar size={15} style={{ color: BLUE }} /> {appointment.date}
          </div>
          {appointment.time && (
            <div className="flex items-center gap-2 text-[13px] font-semibold" style={{ color: NAVY }}>
              <Clock size={15} style={{ color: BLUE }} /> {appointment.time}
            </div>
          )}
          <div className="flex items-center gap-2 text-[12px] text-gray-500">
            {isVirtual ? <Video size={13} className="text-gray-400" /> : <Building size={13} className="text-gray-400" />}
            {isVirtual ? t('deliver.virtualVisit') : t('deliver.inPersonVisit')}
            {appointment.doctorName && <span>• {appointment.doctorName}</span>}
          </div>
        </div>
        
        {!isVirtual && appointment.practiceName && (
          <div className="bg-[#E9F8FF] rounded-xl p-3 mb-2.5 flex items-start gap-2.5">
            <MapPin size={16} style={{ color: BLUE }} className="flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-[12px] font-bold text-gray-700">{appointment.practiceName}</p>
              {/* v222.4.8 — defensive: practiceAddress may be a JSONB dict / JSON-string */}
              {appointment.practiceAddress && <p className="text-[11px] text-gray-500 mt-0.5">{formatAddress(appointment.practiceAddress)}</p>}
              {(appointment.practiceCity || appointment.practiceState) && (
                <p className="text-[11px] text-gray-500">{appointment.practiceCity}{appointment.practiceState ? `, ${appointment.practiceState}` : ''} {appointment.practiceZip || ''}</p>
              )}
              {appointment.practicePhone && (
                <div className="flex items-center gap-1 mt-1">
                  <Phone size={11} className="text-gray-400" />
                  <a href={`tel:${appointment.practicePhone}`} className="text-[11px] font-semibold" style={{ color: BLUE }}>{appointment.practicePhone}</a>
                </div>
              )}
            </div>
          </div>
        )}
        
        {showJoin && (
          <JoinVideoVisit
            appointment={joinTarget}
            className="flex items-center justify-center gap-2 w-full py-2.5 mb-2.5 rounded-xl text-white text-[13px] font-bold no-underline"
            style={{ backgroundColor: '#0B5CFF' }}
            icon={<Video size={16} />}
            trailing={<ExternalLink size={13} />}
            beforeOpen={onJoinCheckIn}
          />
        )}
        
        <button onClick={onReschedule} className="flex items-center gap-1.5 text-[12px] font-semibold border-none bg-transparent cursor-pointer" style={{ color: BLUE }}>
          <RefreshCw size={13} /> {t('deliver.reschedule', 'Reschedule')}
        </button>
        
        {appointment.scheduledBy && (
          <div className="mt-2 pt-2 border-t border-gray-100">
            <span className="text-[10px] text-gray-400">
              {t('deliver.scheduledBy', 'Scheduled by')} <span className="font-semibold text-gray-500">
                {appointment.scheduledBy === 'doctor' ? (appointment.doctorName || 'Doctor') : (appointment.scheduledByName || 'You')}
              </span>
            </span>
          </div>
        )}
      </div>
    </div>
  )
}

// ============================================
// FOLLOW-UP SCHEDULE CARD
// ============================================
function FollowUpScheduleCard({ timing: timingRaw, visitType, onSchedule }) {
  const { t } = useI18n()
  const timing = asText(timingRaw) // PROD-1156
  const visitLabel = visitType === 'in-person' ? 'In-Person' : visitType === 'virtual' || visitType === 'telehealth' ? 'Virtual' : null
  return (
    <div className="rounded-[16px] overflow-hidden border-2 bg-white mb-3" style={{ borderColor: '#A7D9FC' }}>
      <div className="h-[3px]" style={{ backgroundColor: BLUE }} />
      <div className="p-4">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ backgroundColor: ICE_BLUE }}>
            <Calendar size={22} style={{ color: BLUE }} />
          </div>
          <div>
            <h3 className="font-bold text-[15px]" style={{ color: NAVY }}>{t('deliver.followUpAppt')}</h3>
            {timing && (
              <div className="flex items-center gap-1.5 mt-1">
                <Clock size={12} style={{ color: BLUE }} />
                <span className="text-[11px] font-bold px-2 py-[3px] rounded-lg" style={{ backgroundColor: ICE_BLUE, color: '#0284C7' }}>
                  {t('deliver.visitInfo.recommendedWithin', 'Recommended within')} {timing}
                </span>
              </div>
            )}
          </div>
        </div>
        {visitLabel && (
          <div className="bg-gray-50 rounded-xl p-2.5 mb-3 flex items-center gap-2">
            {visitType === 'in-person' ? <Monitor size={14} className="text-gray-500" /> : <Eye size={14} className="text-gray-500" />}
            <span className="text-[11px] text-gray-500">{t('deliver.doctorRecommends', 'Doctor recommends:')} <strong className="text-gray-700">{visitLabel} Visit</strong></span>
          </div>
        )}
        <button onClick={onSchedule} className="w-full py-3 rounded-full font-bold text-[14px] flex items-center justify-center gap-2 text-white border-none cursor-pointer" style={{ backgroundColor: BLUE }}>
          <Calendar size={16} /> {t('deliver.scheduleFollowUpAppointment', 'Schedule Follow-Up Appointment')}
        </button>
      </div>
    </div>
  )
}

// ============================================
// STATE-SPECIFIC CARDS
// ============================================
function ObservationCard({ notes: notesRaw, timing, followUp, deliverData }) {
  const { t } = useI18n()
  // ★ PROD-1156: every plan field this card draws is text (utils/asText.js), never an object.
  const notes = asText(notesRaw)
  const checkInFrequency = asFirst(followUp?.checkInFrequency, deliverData?.checkInSchedule)
  const redFlags = asFirst(followUp?.redFlags, deliverData?.escalationCriteria, followUp?.escalationCriteria)
  const duration = asFirst(followUp?.observationDuration, deliverData?.observationInterval, timing)
  const endDate = deliverData?.observationEndDate
  const startDate = deliverData?.observationStartDate
  // v135.3.0 PA-C2: Structured parameters from encounter care_plan
  const parameters = deliverData?.observationParameters
  
  const frequencyLabels = { daily: t('deliver.daily'), every_other_day: t('deliver.everyOtherDay'), every_3_days: 'Every 3 Days', twice_weekly: t('deliver.twiceWeekly'), weekly: t('deliver.weekly'), bi_weekly: t('deliver.biWeekly'), biweekly: t('deliver.biWeekly'), monthly: t('deliver.monthly') }
  const intervalLabels = { '1_week': '1 Week', '2_weeks': '2 Weeks', '30_days': '30 Days', '60_days': '60 Days', '90_days': '90 Days' }
  
  // ★ dateUtils OWNS THE CALENDAR (ORELLA_STATE 3). `formatDate` is already imported here
  //   and already pins ORELLA_TZ internally — a second, locally-spelled formatter would be
  //   a second source of truth for the one date the patient reads off this card.
  const endLabel = endDate ? formatDate(endDate, { month: 'short', day: 'numeric' }) : ''

  // Countdown
  const countdown = (() => {
    if (!endDate) return null
    const end = new Date(endDate)
    const now = new Date()
    const diffMs = end - now
    if (diffMs <= 0) return { expired: true, days: 0, percent: 100 }
    const days = Math.ceil(diffMs / (1000 * 60 * 60 * 24))
    // ★★★ THIS FALLBACK USED TO BE A HARD 14 DAYS whatever the doctor chose, so a 1-week
    //   window with no stored start date rendered ~93% elapsed on day 6 — the bar looked
    //   finished while the patient still had a day. D332(i) makes `observationStartDate`
    //   reliable going forward; this keeps legacy rows honest.
    const intervalDays = { '1_week': 7, '2_weeks': 14, '30_days': 30, '60_days': 60, '90_days': 90 }[duration] || 14
    const start = startDate ? new Date(startDate) : new Date(end - intervalDays * 24 * 60 * 60 * 1000)
    const totalMs = end - start
    const percent = Math.min(100, Math.max(0, ((now - start) / totalMs) * 100))
    return { expired: false, days, percent: Math.round(percent) }
  })()
  
  // Parse notes into bullet points
  const watchForItems = (() => {
    if (!notes) return []
    return splitClinicalList(notes).filter(s => s.length > 5)
  })()
  
  // Parse red flags into bullet points
  const urgentItems = (() => {
    if (!redFlags) return []
    return splitClinicalList(redFlags).filter(s => s.length > 5)
  })()
  
  // ★★★ ONE CARD LANGUAGE. This rendered its own 16px shell, an amber 2px border, an amber
  //   3px cap, an amber tile, an amber countdown and a red bullet list — a whole second
  //   vocabulary for what is, to a patient, the same kind of object as the cards beneath
  //   it. It is a CareCard instance now. Amber leaves: it is not a brand token, and on this
  //   very screen it still means `asap`/`required`, which is the opposite of what an
  //   observation asks of someone.
  const strip = countdown ? {
    label: countdown.expired ? t('deliver.intervalExpired') : `Ends ${endLabel}`,
    percent: countdown.percent,
  } : null

  const watchList = watchForItems.length > 0 ? watchForItems : (notes ? [notes] : [])
  const urgentList = urgentItems.length > 0 ? urgentItems : (redFlags ? [redFlags] : [])

  return (
    <CareCard
      tone="observation"
      icon={Eye}
      title={t('deliver.observationPeriod')}
      subtitle={`${intervalLabels[duration] || duration || '2 Weeks'}${checkInFrequency ? ` · ${frequencyLabels[checkInFrequency] || checkInFrequency}` : ''}`}
      pill="scheduled"
      pillLabel={countdown && !countdown.expired ? `${countdown.days} DAY${countdown.days !== 1 ? 'S' : ''} LEFT` : null}
      strip={strip}
      items={watchList.map((text, i) => ({ id: `w${i}`, text }))}
      checkedIds={[]}
      plate={urgentList.length > 0 ? { heading: t('deliver.followUpUrgently'), items: urgentList } : null}
      meta={observationParametersMeta(parameters)}
    />
  )
}

// ★ PROD-1156: the observation parameters line. md writes `parameters` as a LIST (md
//   useCasesStore `fu.parameters || []`, API care_plan `next_plan.get('parameters', [])`): its
//   scalar items, joined. This line used to print Object.entries with JSON.stringify, which put
//   index numbers or key names and braces on the card (a list read "0: ... · 1: ..."). An object
//   reads through asText (a readable field, else nothing): never a key name, never JSON.
function observationParametersMeta(parameters) {
  if (!parameters || typeof parameters !== 'object') return null
  const parts = asTextList(parameters)
  return parts.length > 0 ? parts.join(' · ') : null
}

// v124.5.0: Check-Up Card — shows the next due check-up (only one at a time, next appears after completion)
// ★★★ D332(o) — "CHECK-UP", NOT "CHECK-IN". Dr. Rami: the word check-in already means
//   arriving for an appointment in this app — `checkIn.checkInForVisit` is a whole
//   namespace of it — and this card is a CLINICAL check-up the system runs on its own.
//   Two different things wearing one word on one patient's screen is the confusion.
//   ★★ AND THESE WERE HARDCODED ENGLISH on a card every observation patient sees daily,
//     so the screen was untranslatable in exactly that state. Keys now, both locales.
function CheckUpCard({ number, dueDate, onTap, loading }) {
  const { t } = useI18n()
  return (
    <div className="rounded-[14px] overflow-hidden border-2 bg-white mb-3" style={{ borderColor: LIGHT_BLUE }}>
      <div className="h-[2px]" style={{ backgroundColor: BLUE }} />
      <button onClick={onTap} disabled={loading} className="flex items-center p-3 gap-2.5 w-full text-left cursor-pointer bg-transparent border-none disabled:opacity-70">
        {/* ★★ D332(q) — THE TILE STEPS OUT OF THE WAY WHILE IT SPINS. The ring's own track
            is ice, so on an ice tile it was an invisible circle with a blue arc chasing
            nothing. Loading, the tile is transparent and the branded ring is the whole
            of it; loaded, the tile returns with its glyph. */}
        <div className="w-10 h-10 rounded-xl flex items-center justify-center"
             style={{ backgroundColor: loading ? 'transparent' : ICE_BLUE }}>
          {loading ? (
            <OrellaSpinner size={30} ringWidth={2.5} />
          ) : (
            <MessageSquare size={20} style={{ color: BLUE }} />
          )}
        </div>
        <div className="flex-1">
          <p className="text-[14px] font-bold" style={{ color: NAVY }}>{loading ? t('deliver.checkUpPreparing') : t('deliver.checkUpNumber', { number })}</p>
          <div className="flex items-center gap-1 mt-0.5">
            <Clock size={11} className="text-gray-400" />
            <span className="text-[11px] text-gray-400">{loading ? t('deliver.checkUpLoadingQuestions') : t('deliver.checkUpDueOn', { date: dueDate })}</span>
          </div>
        </div>
        {!loading && <span className="text-[9px] font-bold px-2.5 py-1 rounded-md border" style={{ backgroundColor: ICE_BLUE, color: BLUE, borderColor: LIGHT_BLUE }}>{t('deliver.due')}</span>}
        {!loading && <ChevronRight size={18} className="text-gray-300" />}
      </button>
    </div>
  )
}

function CareCompleteCard({ isPCP, onRequestFollowUp, signOutNotes: signOutRaw, reasonsToReturn: reasonsRaw, isReopening, avsContent, onDownloadAvs }) {
  const { t } = useI18n()
  const signOutNotes = asText(signOutRaw) // PROD-1156
  const reasonsToReturn = asText(reasonsRaw) // PROD-1156
  // ★★★ SAME LANGUAGE AS THE OBSERVATION CARD. This cannot literally be a CareCard — it
  //   holds two buttons and CareCard's shell IS a button, so nesting them would be invalid
  //   markup. What matters is shared anyway: the shell, the green identity, and above all
  //   the PLATE, so a patient meets one pattern for "conditions that should bring you back"
  //   whether they are under observation or discharged.
  //   Amber and the slate fills are gone: amber is not a brand token, and v243.81.0 ruled
  //   out grey backgrounds anywhere in the app.
  return (
    <div className="rounded-2xl border bg-white mb-3" style={{ borderColor: '#BBE9BF' }}>
      <div className="p-[13px]">
        <div className="flex items-start gap-3 mb-3">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#66D170' }}>
            <CheckCircle size={16} style={{ color: '#FFFFFF' }} />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[14.5px] font-bold leading-tight" style={{ color: '#15354F' }}>{t('deliver.careComplete')}</p>
            <p className="text-[11px] text-slate-500 mt-0.5">{isPCP ? 'Return to PCP' : t('deliver.resolved')}</p>
          </div>
          <span className="flex-none text-[9.5px] font-extrabold uppercase tracking-wide px-[11px] py-1 rounded-full border whitespace-nowrap"
                style={{ backgroundColor: '#D5EFD3', color: '#16A34A', borderColor: '#BBE9BF' }}>
            {t('deliver.resolved')}
          </span>
        </div>
        {/* Sign-out notes from encounter */}
        {signOutNotes ? (
          <div className="rounded-xl p-3 mb-2 border" style={{ backgroundColor: '#E9F8FF', borderColor: '#A7D9FC' }}>
            <p className="text-[11px] font-bold mb-1" style={{ color: '#15354F' }}>{t('deliver.dischargeSummary')}</p>
            <p className="text-[13px] text-slate-600 leading-relaxed">{signOutNotes}</p>
          </div>
        ) : (
          <div className="rounded-xl p-3 mb-2 border" style={{ backgroundColor: '#E9F8FF', borderColor: '#A7D9FC' }}>
            <p className="text-[11px] font-bold mb-1" style={{ color: '#15354F' }}>{t('deliver.whenToReturn')}</p>
            <p className="text-[13px] text-slate-600 leading-relaxed">
              {isPCP ? 'Your care has been transitioned to your primary care doctor. Contact your PCP for ongoing concerns or routine follow-up.'
                : 'Your condition has been addressed. Return if symptoms recur, worsen, or you develop new concerns.'}
            </p>
          </div>
        )}
        {/* Reasons to return from encounter */}
        {/* ★★ THE PLATE — the same object the observation card uses, ice inside a navy
            border. It was amber-on-amber, which on this screen still reads as `asap`. */}
        {reasonsToReturn && (
          <div className="rounded-[11px] px-3 py-[11px] mb-3 border"
               style={{ backgroundColor: '#E9F8FF', borderColor: '#15354F' }}>
            <p className="text-[9.5px] font-extrabold uppercase tracking-[0.1em] m-0 mb-1.5"
               style={{ color: '#15354F' }}>{t('deliver.seekCareIf')}</p>
            <ul className="list-none p-0 m-0">
              {splitClinicalList(reasonsToReturn).filter(x => x.length > 3).map((line, i) => (
                <li key={i} className="flex items-start gap-2 py-[3px] text-[12.5px] leading-normal" style={{ color: '#15354F' }}>
                  <span className="flex-none mt-[7px] w-[5px] h-[5px] rounded-full block" style={{ backgroundColor: '#15354F' }} />
                  <span className="min-w-0 break-words">{line}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        {/* v135.2.0 PA-C3: AVS download button — from encounter data */}
        {avsContent && onDownloadAvs && (
          <button onClick={onDownloadAvs}
            className="w-full py-2.5 mb-2 border-2 border-emerald-200 text-emerald-700 rounded-full text-[13px] font-bold flex items-center justify-center gap-2 active:bg-emerald-50 bg-white cursor-pointer">
            <FileText size={15} /> {t('deliver.downloadAfterVisitSummary', 'Download After Visit Summary')}
          </button>
        )}
        <button onClick={onRequestFollowUp} disabled={isReopening}
          className="w-full py-2.5 border-2 border-slate-200 text-slate-600 rounded-full text-[13px] font-bold flex items-center justify-center gap-2 active:bg-slate-50 bg-transparent cursor-pointer disabled:opacity-50">
          {isReopening 
            ? <><OrellaSpinner size={16} tone="inherit" /> {t('deliver.reopeningCase', 'Reopening Case...')}</>
            : <><Calendar size={15} /> {t('deliver.requestFollowUpIfNeeded', 'Request Follow-Up If Needed')}</>
          }
        </button>
      </div>
    </div>
  )
}

function ReferralCard({ referralData, followUp, referralRecord, caseId, onViewReferralLetter }) {
  const { t } = useI18n()
  // ★ PROD-1156: the plan's referral fields are drawn as text (utils/asText.js), never as an object.
  const specialty = asFirst(referralData?.specialty, followUp?.referralSpecialty) || t('deliver.specialist')
  const notes = asFirst(referralData?.notes, followUp?.referralNotes)
  const status = referralRecord?.status || 'created'
  const hasStartedCase = status === 'accepted' || status === 'scheduled' || status === 'completed'
  const referringDoctor = asFirst(referralRecord?.referring_doctor, referralRecord?.referringDoctor)
  const clinicalReason = asFirst(referralRecord?.clinical_reason, referralRecord?.clinicalReason) || notes

  // v135.3.0 PA-C4: Status badge colors
  const statusConfig = {
    created: { label: t('deliver.pending'), bg: '#FEF3C7', text: '#92400E', border: '#FDE68A' },
    sent: { label: t('deliver.sent'), bg: '#DBEAFE', text: '#1E40AF', border: '#BFDBFE' },
    accepted: { label: t('deliver.accepted'), bg: '#D1FAE5', text: '#065F46', border: '#A7F3D0' },
    scheduled: { label: t('deliver.scheduled'), bg: '#D1FAE5', text: '#065F46', border: '#A7F3D0' },
    completed: { label: t('deliver.completed'), bg: '#D1FAE5', text: '#065F46', border: '#A7F3D0' },
    case_created: { label: t('deliver.caseStarted'), bg: '#DBEAFE', text: '#1E40AF', border: '#BFDBFE' },
    patient_claimed: { label: t('deliver.inProgress'), bg: '#DBEAFE', text: '#1E40AF', border: '#BFDBFE' },
    in_progress: { label: t('deliver.inProgress'), bg: '#DBEAFE', text: '#1E40AF', border: '#BFDBFE' },
  }
  const badge = statusConfig[status] || statusConfig.created
  
  return (
    <div className="rounded-[16px] overflow-hidden bg-white mb-3" style={{ border: '1.5px solid #A7D9FC' }}>
      {/* v242.101.0 — teal retired; the referral card moves to the blue family. */}
      <div className="h-[3px]" style={{ backgroundColor: '#46A8F9' }} />
      <div className="p-4">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#E9F8FF' }}>
            <Send size={22} style={{ color: '#46A8F9' }} />
          </div>
          <div className="flex-1">
            <h3 className="font-bold text-gray-800 text-[15px]">{t('deliver.specialistReferral')}</h3>
            <span className="text-[10px] font-bold px-2 py-[3px] rounded-lg" style={{ backgroundColor: '#E9F8FF', color: '#15354F' }}>{t('deliver.referredTo', 'Referred to')} {specialty}</span>
          </div>
          {/* v135.3.0 PA-C4: Status badge */}
          <span className="text-[9px] font-bold px-2 py-1 rounded-md border"
            style={{ backgroundColor: badge.bg, color: badge.text, borderColor: badge.border }}>
            {badge.label}
          </span>
        </div>
        <div className="rounded-xl p-3 mb-2" style={{ backgroundColor: '#E9F8FF' }}>
          <p className="text-[11px] font-bold mb-1" style={{ color: '#15354F' }}>{t('deliver.referralDetails')}</p>
          <p className="text-[13px] leading-relaxed" style={{ color: '#15354F' }}>
            {t('deliver.referredTo', 'Referred to')} <strong>{specialty}</strong> for further evaluation.
            {referringDoctor ? ` Referred by ${clinicianName(clinicianFromText(referringDoctor))}.` : ''}
          </p>
        </div>
        {clinicalReason && (
          <div className="rounded-xl p-3 mb-2" style={{ backgroundColor: '#E9F8FF80' }}>
            <p className="text-[11px] font-bold mb-1" style={{ color: '#15354F' }}>{t('deliver.clinicalReason')}</p>
            <p className="text-[13px] leading-relaxed" style={{ color: '#15354F' }}>{clinicalReason}</p>
          </div>
        )}
        {/* v240.0.0 (REF-AK): patient-initiated "Start New Case" referral button removed.
            Referral cases are spawned MD-side via the gated POST /referrals flow. */}
        {/* v240.0.2 (REF-O): view the referral letter already on this case */}
        {onViewReferralLetter && (
          <button
            onClick={() => onViewReferralLetter(referralRecord || referralData)}
            className="w-full py-2.5 rounded-full font-bold text-[13px] flex items-center justify-center gap-2 mt-1 border cursor-pointer"
            style={{ borderColor: '#15354F', color: '#15354F', backgroundColor: '#fff' }}
          >
            <FileText size={15} /> {t('deliver.viewReferralLetter', 'View referral letter')}
          </button>
        )}
        {hasStartedCase && (
          <div className="bg-green-50 rounded-xl p-2.5 text-center mt-1">
            <p className="text-[12px] font-semibold text-green-700">
              <CheckCircle size={13} className="inline mr-1" />
              {t('deliver.referralCase', 'Referral case')} {status === 'completed' ? 'completed' : 'in progress'}
            </p>
          </div>
        )}
      </div>
    </div>
  )
}

// ============================================
// DOCTOR MINI CARD + PROFILE MODAL
// ============================================
// ★★★ v242.121.0 · DOCTOR-ROW-OPENS-INLINE-1 — Dr. Rami: "I'd like for the doctor
//   photo and office information to open in the doctor row."
//   ★★ THE OFFICE WAS NEVER MISSING FROM THE DATA — practice name, address, city,
//     state, zip and phone all ride on sentToDoctorData and on the appointment record.
//     The row drew none of it and sent you to a sheet to read it.
//   ★ THE PHOTO ALSO ALREADY RENDERS when the record carries one; initials are the
//     honest fallback for a doctor with no photo on file, not a bug to paper over.
function DoctorMiniCard({ doctor, onViewProfile }) {
  const { t } = useI18n()
  const [open, setOpen] = useState(false)
  if (!doctor) return null

  const address = [formatAddress(doctor.practice_address), doctor.practice_city, doctor.practice_state, doctor.practice_zip]
    .filter(Boolean).join(', ')
  const phone = doctor.phone || doctor.practice_phone
  const hasOffice = !!(doctor.practice_name || address || phone)

  return (
    <div className="bg-white rounded-2xl border border-[#15354F] mb-2.5 p-[13px] pb-[11px]">
      <button type="button"
        onClick={() => (hasOffice ? setOpen(o => !o) : onViewProfile?.())}
        className="w-full flex items-center gap-3 bg-transparent border-none p-0 text-left cursor-pointer">
        <div className="w-11 h-11 rounded-full bg-[#E9F8FF] flex items-center justify-center overflow-hidden flex-shrink-0">
          {doctor.profile_photo
            ? <img src={doctor.profile_photo} alt="" className="w-full h-full object-cover" />
            : <span className="text-[#46A8F9] font-bold text-[14px]">{doctor.first_name?.[0]}{doctor.last_name?.[0]}</span>}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[14.5px] font-bold leading-tight" style={{ color: NAVY }}>{clinicianName(doctor)}</p>
          <p className="text-[11px] text-slate-500 mt-0.5">
            {formatProviderSpecialty((doctor.specialties && doctor.specialties[0]) || doctor.specialty || '', t)}
          </p>
        </div>
        {hasOffice
          ? <ChevronRight size={16} className={`text-slate-300 flex-shrink-0 transition-transform ${open ? 'rotate-90' : ''}`} />
          : onViewProfile && <User size={14} className="text-slate-300 flex-shrink-0" />}
      </button>

      {open && hasOffice && (
        <div className="mt-2.5 pt-2.5 border-t border-slate-100">
          {doctor.practice_name && (
            <p className="text-[12.5px] font-semibold" style={{ color: NAVY }}>{doctor.practice_name}</p>
          )}
          {address && (
            <p className="text-[11.5px] text-slate-500 mt-0.5 leading-relaxed flex items-start gap-1.5">
              <MapPin size={12} style={{ color: BLUE }} className="flex-shrink-0 mt-[3px]" />
              <span>{address}</span>
            </p>
          )}
          {phone && (
            <a href={`tel:${phone}`} onClick={(e) => e.stopPropagation()}
              className="text-[11.5px] font-semibold mt-1.5 inline-flex items-center gap-1.5 no-underline" style={{ color: BLUE }}>
              <Phone size={12} /> {phone}
            </a>
          )}
          {onViewProfile && (
            <button type="button" onClick={onViewProfile}
              className="mt-2.5 pt-2.5 border-t border-slate-100 w-full flex items-center gap-1.5 text-[11.5px] font-semibold bg-transparent border-x-0 border-b-0 cursor-pointer" style={{ color: BLUE }}>
              <User size={12} /> {t('deliver.viewFullProfile')}
              <ChevronRight size={13} className="ml-auto text-slate-300" />
            </button>
          )}
        </div>
      )}
    </div>
  )
}

// ★★★ v242.118.0 · DELIVER-DOCTORPROFILE-ABBREVIATED-1 — Dr. Rami, on the Deliver
//   screen: "when I click the profile for the doctor on the page it abbreviated, not
//   the full profile in the app."
//   ★★★ THE FULL PROFILE WAS ALREADY WIRED — ON THE OTHER BRANCH. The closed-case
//     return opened DoctorProfileScreen in a sheet when the record carried an id, and
//     fell back to the local summary card only when it did not. The MAIN Deliver
//     return rendered the summary card UNCONDITIONALLY, so on the screen a patient
//     actually uses, the full profile was unreachable — with a perfectly good id
//     sitting in sentToDoctorData, which ShareScreen has always written.
//   ★★★ A CORRECTION APPLIED WHERE IT WAS REPORTED IS NOT A CORRECTION. Two renders
//     of one decision is how the two branches drifted, so BOTH SITES NOW RENDER THIS
//     and there is nothing left to keep in sync.
//   ★★ THE FALLBACK IS STILL HONEST: no id means there is no doctor record to fetch —
//     an external doctor, or a case that never went through the share flow — and the
//     summary card is then all there is to show.
function DoctorProfileHost({ doctor, onClose }) {
  if (!doctor) return null
  if (!doctor.id) return <DoctorProfileModal doctor={doctor} onClose={onClose} />
  return (
    <BottomSheet open onClose={onClose} maxHeight="92dvh">
      <DoctorProfileScreen
        asSheet
        doctorIdProp={doctor.id}
        modeProp="view"
        onCloseProp={onClose}
      />
    </BottomSheet>
  )
}

function DoctorProfileModal({ doctor, onClose }) {
  const { t } = useI18n()
  if (!doctor) return null
  return createPortal(
    /* data-no-top-inset: the panel below is `mt-auto`, so this overlay is
       bottom-anchored and its content never reaches the status bar. Declared
       rather than silently exempted, because scripts/check-safe-area.mjs
       requires every full-bleed portal to answer the question one way or the
       other — an overlay that neither insets nor explains is the bug. */
    <div className="fixed inset-0 z-50 flex flex-col bg-black/40" data-no-top-inset="bottom-anchored panel (mt-auto)" onClick={onClose}>
      <div className="mt-auto mx-4 mb-6 bg-white rounded-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}
        style={{ animation: 'fadeSlideUp 0.35s cubic-bezier(0.16,1,0.3,1)' }}>
        <div className="p-5 text-center">
          <div className="w-16 h-16 rounded-full mx-auto mb-3 bg-[#E9F8FF] flex items-center justify-center overflow-hidden">
            {doctor.profile_photo
              ? <img src={doctor.profile_photo} alt="" className="w-full h-full object-cover" />
              : <span style={{ color: BLUE }} className="font-bold text-xl">{doctor.first_name?.[0]}{doctor.last_name?.[0]}</span>
            }
          </div>
          <h3 className="text-[17px] font-bold" style={{ color: NAVY }}>{clinicianName(doctor)}</h3>
          <p className="text-[13px] font-semibold mt-0.5" style={{ color: BLUE }}>{((doctor.specialties && doctor.specialties[0]) || '')}</p>
          {doctor.years_experience && <p className="text-[12px] text-gray-400 mt-1">{doctor.years_experience} years experience</p>}
          {doctor.medical_school && <p className="text-[12px] text-gray-400">{doctor.medical_school}</p>}
          {doctor.practice_name && (
            <div className="bg-gray-50 rounded-xl p-3 mt-3 text-left">
              <p className="text-[12px] font-bold text-gray-700">{doctor.practice_name}</p>
              {/* v222.4.8 — defensive: practice_address may be a JSONB dict / JSON-string */}
              {doctor.practice_address && <p className="text-[11px] text-gray-500 mt-0.5">{formatAddress(doctor.practice_address)}</p>}
              {doctor.practice_phone && (
                <a href={`tel:${doctor.practice_phone}`} className="text-[11px] font-semibold mt-1 flex items-center gap-1 no-underline" style={{ color: BLUE }}>
                  <Phone size={11} /> {doctor.practice_phone}
                </a>
              )}
            </div>
          )}
        </div>
        <button onClick={onClose} className="w-full py-3.5 border-t border-gray-100 text-[14px] font-bold border-l-0 border-r-0 border-b-0 bg-transparent cursor-pointer" style={{ color: BLUE }}>{t('deliver.close')}</button>
      </div>
    </div>,
    document.body
  )
}

// ============================================
// MAIN: UNIFIED DELIVER SCREEN
// ============================================
export default function DeliverScreen() {
  const navigate = useNavigate()
  const location = useLocation()
  const { currentCase, updateCase, updateAndSaveCase } = useCaseStore()
  const { t, language, hasText } = useI18n()
  const appointmentErrorKey = (e) => appointmentErrorKeyFor(e, hasText) // PROD-1089: es withholds the physician_inactive sentence
  const concernSentMessageKey = (c) => concernSentMessageKeyFor(c, hasText)
  const { openAgent } = useAgent()
  // v173.0.0: Safe translation with fallback
  const ts = (key, fallback) => { const v = t(key); return (!v || v === key) ? fallback : v }

  // Sequential step machine — gated progression
  // v173.0.0: Step machine now supports unified flow:
  // Unified: 0=greeting, 1=doctor, 2=cards, 3=voice(GATE), 4=generating+questions bubble, 5=questions section, 6=submit
  // Fallback: 0=greeting, 1=doctor, 2=cards, 3=voice(GATE), 4=symptoms(GATE), 5=meds(GATE), 6=submit
  const [step, setStep] = useState(0)
  const [showDoctorProfile, setShowDoctorProfile] = useState(null)
  const [schedulingInline, setSchedulingInline] = useState(false)
  const [activeModal, setActiveModal] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [submitPhase, setSubmitPhase] = useState(null)

  // Pre-visit report state — load persisted data from case store
  const deliver = currentCase?.deliver || {}
  const [voiceMemo, setVoiceMemo] = useState(deliver.voiceMemoData || null)
  const [symptomSurvey, setSymptomSurvey] = useState(deliver.symptomSurveyData || null)
  const [medicationReview, setMedicationReview] = useState(deliver.medicationReviewData || null)

  // v135.1.0 PA-D3: Ref for debounced encounter pre-visit sync timer
  const encounterSyncTimerRef = useRef(null)

  useEffect(() => { window.scrollTo(0, 0); return () => { if (encounterSyncTimerRef.current) clearTimeout(encounterSyncTimerRef.current) } }, [])

  // v234.16.0: Destination backstop — the managed Deliver phase cannot run without an
  // Orella-subscribed doctor. Whatever routed here (encounter timeline, ?openReport deep link,
  // Ask Orella agent, browser back), an MD-agnostic case is sent back to Case Home. This single
  // guard makes every current and future Deliver entry point safe — callers needn't each re-gate.
  // ★ PROD-1079: the same backstop while the case has no signed visit. Before signing,
  //   cases.follow_up is md's draft plan (utils/signedPlan.js); a deep link, the agent or a
  //   notification must not open it as the patient's task list.
  useEffect(() => {
    if (currentCase && (deliverNeedsOrellaMD(currentCase) || !caseHasSignedVisit(currentCase))) {
      navigate('/case/home', { replace: true })
    }
  }, [currentCase, navigate])

  // Auto-advance steps 0→3 (greeting cascade), then STOP at each gate
  // v124.7.1: For observation, continue to step 5 (no pre-visit gates)
  useEffect(() => {
    if (step >= 1 && step <= 3) {
      const t = setTimeout(() => setStep(s => s + 1), 450)
      return () => clearTimeout(t)
    }
    // Observation: no gates, keep cascading through 4→5
    // Use currentCase.status directly (deliverState isn't defined yet at this point)
    // ★ deliverData is declared below this effect, so this call is deliberately one-arg.
    const isObservation = isObservationCase(currentCase)
    if (isObservation && step >= 4 && step <= 5) {
      const t = setTimeout(() => setStep(s => s + 1), 400)
      return () => clearTimeout(t)
    }
  }, [step, currentCase?.status])

  // On mount: skip past completed gates so returning users see where they left off
  // v173.0.0: Also check for unified questions data
  useEffect(() => {
    if (!currentCase) return
    const d = currentCase.deliver || {}
    let resumeStep = 3 // default: auto-cascade gets us to voice
    
    // Check if unified questions were already loaded in a previous session
    if (d.unifiedQuestionsData?.length >= 2) {
      setUnifiedQuestions(d.unifiedQuestionsData)
      setUseUnifiedFlow(true)
      unifiedFetchedRef.current = true
      if (d.unifiedAnswersData) setUnifiedAnswers(d.unifiedAnswersData)
      if (d.unifiedDetailsData) setUnifiedDetails(d.unifiedDetailsData)
    }
    
    if (d.voiceMemoData || d.voiceSkipped) {
      // v173.0.0: If unified flow was used, skip to unified questions (step 5)
      if (d.unifiedQuestionsData?.length >= 2) {
        resumeStep = 5 // Show unified questions
        if (d.unifiedAnswersData && Object.keys(d.unifiedAnswersData).length > 0) {
          resumeStep = 6 // Show submit
        }
      } else {
        resumeStep = 4
      }
    }
    if (!d.unifiedQuestionsData && (d.voiceMemoData || d.voiceSkipped) && (d.symptomSurveyData || d.symptomSkipped)) resumeStep = 5
    if (!d.unifiedQuestionsData && (d.voiceMemoData || d.voiceSkipped) && (d.symptomSurveyData || d.symptomSkipped) && 
        (d.medicationReviewData || d.medSkipped || !(signedOrdersPlaced(currentCase).medications?.length > 0))) resumeStep = 6
    if (resumeStep > 3) {
      const t = setTimeout(() => setStep(resumeStep), 500)
      return () => clearTimeout(t)
    }
  }, [])

  // Save helper — persists pre-visit data to case store
  const saveDeliverData = (patch) => {
    updateAndSaveCase({ deliver: { ...deliver, ...patch } })
  }


  // v135.1.0 PA-D3: Background sync of pre-visit data to encounter's pre_visit_report
  // Debounced — fires 2s after last step completion to avoid rapid-fire API calls
  const syncPreVisitToEncounter = (updatedDeliver) => {
    if (!currentCase?.id) return
    const encNum = currentEncounterNumber(currentCase)
    if (encNum < 2) return // Only sync for follow-up encounters (enc >= 2)
    
    if (encounterSyncTimerRef.current) clearTimeout(encounterSyncTimerRef.current)
    encounterSyncTimerRef.current = setTimeout(async () => {
      try {
        const d = { ...deliver, ...updatedDeliver }
        const partialReport = {
          voiceTranscript: d.voiceMemoData?.transcript || '',
          // ★★★ FU-SYNC-CARRIES-AUDIO-1 (D281). This debounced background save is
          //     fired from six call sites and used to carry the transcript, the
          //     survey, the medication review and the care orders — and NOT the
          //     audio reference. Before API v243.136.0 the server rebuilt the whole
          //     report from whatever a writer sent, so every background save DELETED
          //     the voiceAudioUrl the real submission had just written, and the
          //     patient's recording sat in S3 unreferenced.
          // ★★ The server fix is the real one: _merge_pre_visit_report never deletes,
          //    treats an explicit null as absent, and never lets falsy overwrite
          //    truthy — so sending null here is safe by contract. This is belt and
          //    braces, and the braces are what stops a future rebuild regressing it.
          voiceAudioUrl: d.voiceMemoData?.audioUrl || null,
          symptomSurvey: d.symptomSurveyData || {},
          medicationReview: d.medicationReviewData || {},
          // ★ TEST 2.14: the physician's report reads the SAME per-encounter
          //   careTicks store InstructionsScreen writes, via legacyIds — not
          //   the flat `careOrdersCheckedItems` this background sync itself
          //   never writes (only InstructionsScreen's Save does), so it never
          //   goes stale relative to it.
          careOrders: {
            checkedItems: legacyIds('careOrders', deliverData?.careOrders || [], d.careTicks, `e${encNum}`, dayKey()),
            complete: d.careOrdersComplete || false,
          },
          // (deliverData?.careOrders is the same raw ordersPlaced.careOrders
          // array InstructionsScreen keys against — see careTicks.js's
          // canonicalItemText for why passing it raw here, rather than a
          // pre-extracted string, matters.)
          _partialSave: true, // Flag so API knows this isn't the final submission
          _savedAt: new Date().toISOString(),
        }
        await encountersAPI.submitPreVisitReport(currentCase.id, encNum, partialReport)
        console.log('[Deliver] PA-D3: Background pre-visit sync to encounter', encNum)
      } catch (err) {
        console.warn('[Deliver] PA-D3: Background sync failed (non-blocking):', err)
      }
    }, 2000)
  }

  // Complete voice memo
  // v173.0.0: FollowUpReportModal now handles the full flow for follow-up cases
  // It calls onSubmit with either 'voice-only' (voice saved, questions next) or 'full-report' (everything done)
  const handleVoiceComplete = async (data, phase) => {
    // v124.0.0: If in report finding mode, capture for observation bridge instead
    if (reportMode) {
      setReportVoiceMemo(data)
      setReportTranscript(data?.transcript || data?.text || '')
      setActiveModal(null)
      setReportStep(2) // advance to scheduling
      return
    }
    
    if (phase === 'voice-only') {
      // Phase 1: Just voice data saved, modal stays open for questions
      setVoiceMemo(data)
      const patch = { voiceMemoData: data, voiceSkipped: false }
      saveDeliverData(patch)
      syncPreVisitToEncounter(patch)
      // Don't close modal or advance step — modal handles the rest
      return
    }
    
    if (phase === 'submit-report') {
      // v174.0.1: Full submit from within the modal — runs standard submission animation
      setVoiceMemo(data)
      const patch = { voiceMemoData: data, voiceSkipped: false }
      
      if (data.followUpQuestions) {
        patch.unifiedQuestionsData = data.followUpQuestions.questions
        patch.unifiedAnswersData = data.followUpQuestions.answers
        patch.unifiedDetailsData = data.followUpQuestions.details
        if (data.symptomSurvey) patch.symptomSurveyData = data.symptomSurvey
        if (data.medicationReview) patch.medicationReviewData = data.medicationReview
      }
      
      saveDeliverData(patch)
      syncPreVisitToEncounter(patch)
      setActiveModal(null)
      setSubmitting(true)
      
      // Run standard submission overlay animation
      // v225.23.0 (Bug 3): track whether the report actually reached the API so a
      // post-submit hiccup (case re-save / overlay timing) can't fire a false
      // "Failed to submit" alert. Pass onComplete to reset submitPhase so the
      // overlay unmounts and the success view renders in place.
      let reportSaved = false
      try {
        await runOverlaySequence(setSubmitPhase, async () => {
          const reportData = {
            voiceTranscript: data.transcript || '',
            voiceAudioUrl: data.audioUrl || null,
            symptomSurvey: data.symptomSurvey || {},
            medicationReview: data.medicationReview || {},
            followUpQuestions: data.followUpQuestions || null,
            scales: Array.isArray(data.scales) ? data.scales : [], // s128: stored on this encounter (pre_visit_report.scales)
            careOrders: {
              items: deliverData?.careOrders || [],
              // ★ TEST 2.14 — see the syncPreVisitToEncounter builder above.
              checkedItems: legacyIds('careOrders', deliverData?.careOrders || [], deliver.careTicks, `e${encounterNumber}`, dayKey()),
              complete: deliver.careOrdersComplete || false,
              savedAt: deliver.careOrdersSavedAt || null,
            },
          }
          const result = await encountersAPI.submitPreVisitReport(currentCase.id, encounterNumber, reportData)
          reportSaved = true
          console.log('[Deliver] v174: Pre-visit report submitted from modal:', !!result?.success) // PROD-903: the answer echoes the report
          try {
            await updateAndSaveCase({ 
              encounterPhase: 'diagnose_pending', 
              deliverState: 'submitted',
              deliver: { ...deliver, ...patch }
            })
          } catch (saveErr) {
            console.warn('[Deliver] post-submit case save failed (non-fatal):', saveErr)
          }
          // v217.1.6: Mark submitted so the success view renders after the
          // overlay clears.
          setSubmitted(true)
        }, () => setSubmitPhase(null))
      } catch (err) {
        if (reportSaved) {
          // Report reached the doctor — a later step in the chain threw, but
          // this is NOT a submission failure. Show success, suppress the alert.
          console.warn('[Deliver] post-submit sequence error (report WAS saved):', err)
          setSubmitted(true)
        } else {
          console.error('[Deliver] Submit from modal failed:', err)
          alert(t('errors2.failedToSubmit'))
        }
      } finally {
        setSubmitting(false)
      }
      return
    }
    
    if (phase === 'full-report') {
      // Phase 2: Full report including unified answers (legacy path, kept for compat)
      setVoiceMemo(data)
      const patch = { voiceMemoData: data, voiceSkipped: false }
      
      // Save unified questions data if present
      if (data.followUpQuestions) {
        patch.unifiedQuestionsData = data.followUpQuestions.questions
        patch.unifiedAnswersData = data.followUpQuestions.answers
        patch.unifiedDetailsData = data.followUpQuestions.details
        setUnifiedQuestions(data.followUpQuestions.questions)
        setUnifiedAnswers(data.followUpQuestions.answers)
        setUnifiedDetails(data.followUpQuestions.details)
        setUseUnifiedFlow(true)
        
        // Also store symptom/medication for backward compat
        if (data.symptomSurvey && Object.keys(data.symptomSurvey).length > 0) {
          setSymptomSurvey(data.symptomSurvey)
          patch.symptomSurveyData = data.symptomSurvey
        }
        if (data.medicationReview && Object.keys(data.medicationReview).length > 0) {
          setMedicationReview(data.medicationReview)
          patch.medicationReviewData = data.medicationReview
        }
      }
      
      saveDeliverData(patch)
      syncPreVisitToEncounter(patch)
      setActiveModal(null)
      // Jump to submit step
      setStep(s => Math.max(s, 6))
      return
    }
    
    // Legacy path (non-follow-up / initial encounter): original behavior
    setVoiceMemo(data)
    const patch = { voiceMemoData: data, voiceSkipped: false }
    saveDeliverData(patch)
    syncPreVisitToEncounter(patch)
    setActiveModal(null)
    setStep(s => Math.max(s, 4))
  }

  // Complete symptom check-in
  const handleSymptomComplete = (data) => {
    // v163.6.0: Tag the source so MD panel knows if AI or static questions were used
    const taggedData = { ...data, _source: bridgeQuestionsSource, _generatedAt: new Date().toISOString() }
    setSymptomSurvey(taggedData)
    const patch = { symptomSurveyData: taggedData, symptomSkipped: false }
    saveDeliverData(patch)
    syncPreVisitToEncounter(patch) // v135.1.0 PA-D3
    setActiveModal(null)
    setStep(s => Math.max(s, 5))
  }

  // Complete medication review
  const handleMedComplete = (data) => {
    const taggedData = { ...data, _source: medAiQuestions ? 'ai+static' : 'static', _generatedAt: new Date().toISOString() }
    setMedicationReview(taggedData)
    const patch = { medicationReviewData: taggedData, medSkipped: false }
    saveDeliverData(patch)
    syncPreVisitToEncounter(patch) // v135.1.0 PA-D3
    setActiveModal(null)
    setStep(s => Math.max(s, 6))
  }

  // Skip handlers
  // v173.0.1: Skip voice for follow-up cases falls back to legacy modals
  const handleSkipVoice = () => { 
    saveDeliverData({ voiceSkipped: true })
    setUnifiedFlowFailed(true) // Show legacy modals since they skipped the modal flow
    setStep(s => Math.max(s, 4))
  }
  const handleSkipSymptom = () => { saveDeliverData({ symptomSkipped: true }); setStep(s => Math.max(s, 5)) }
  const handleSkipMed = () => { saveDeliverData({ medSkipped: true }); setStep(s => Math.max(s, 6)) }

  // ==========================================
  // EXTRACT DELIVER DATA
  // v135.1.0 PA-D2: Extracted to useActiveCarePlan hook — single source of truth
  // Resolves active encounter care_plan with flat followUp fallback
  // ==========================================
  const deliverData = useActiveCarePlan(currentCase)
  // s128 (board D): the scales the clinician confirmed, that the app can ask (free scales + the catalog's scored scales)
  const followUpScales = useMemo(() => followUpScaleOrders(deliverData.scales, scalesLib, questionnairesData), [deliverData.scales])
  // PROD-1079: follow_up with md's plan keys dropped until the case has a signed visit
  const signedFollowUp = planFollowUp(currentCase)

  // ==========================================
  // GENERATE ORDER-BASED TASKS
  // ==========================================
  const orderTasks = useMemo(() => {
    if (!deliverData) return []
    const list = []
    // ★ PROD-1156: an order's line is text; a name that is an object reads through asText.
    const txt = (item) => typeof item === 'string' ? item : asFirst(item?.name, item?.test, item?.description) || 'Order'
    // ★★★ D332(e) — CLOSE-OUT CARE ITEMS ARE OPEN-ENDED, SO THEY CARRY NO DUE CHIP.
    //   His ruling: on an observation or a closed case these are close-out care items —
    //   either ongoing or conditional — and any timing that exists is stated in the
    //   observation or close-out note, not in a pill.
    //   ★★ THIS DISPOSES OF "DUE BY FOLLOW-UP" RATHER THAN RE-COLOURING IT. Every one of
    //     the eight order categories defaults to `by-followup` (CaseDetailPage:1392), and
    //     the fall-through below prints that literal whenever `followUpTiming` is empty —
    //     which on an observation is ALWAYS, because there is no follow-up to be due by.
    //     A chip asserting a deadline that does not exist teaches the patient the chip can
    //     be ignored, which is worse than no chip.
    const plan = String(deliverData.followUpPlan || '').toLowerCase()
    const isCloseOutCare = plan === 'observation' || plan === 'complete' || plan === 'completed'
    //   ★★ D332(n) — AND THIS FUNCTION IS THE ONLY THING THAT DECIDES WHETHER A DUE CHIP
    //     EXISTS. It returns the chip's TONE as well as its text, because the map below
    //     used to re-derive the tone from `dueDates` on its own and defaulted it to
    //     `by-followup` — so removing the chip here left the chip on screen anyway.
    //     Two functions answering one question is how "DUE BY FOLLOW-UP" survived D332(e).
    //     null means NO CHIP, at every renderer, with no fall-through label.
    const dueInfo = (type) => {
      const dd = deliverData.dueDates?.[type]
      if (isCloseOutCare && (!dd || dd === 'by-followup')) return null
      if (!dd || dd === 'by-followup') return { tone: 'by-followup', text: deliverData.followUpTiming ? t('deliver.dueDate', { date: deliverData.followUpTiming }) : t('deliver.dueByFollowUp') }
      if (dd === 'asap') return { tone: 'asap', text: t('deliver.asap'), urgent: true }
      if (dd === 'under-consideration') return { tone: 'under-consideration', text: t('deliver.underConsideration') }
      if (dd === 'custom' && deliverData.customDates?.[type]) return { tone: 'custom', text: `Due: ${formatDate(deliverData.customDates[type], { month: 'short', day: 'numeric' })}` }
      return null
    }

    if (deliverData.careOrders.length > 0) list.push({ id: 'careOrders', type: 'careOrders', icon: Activity, title: t('deliver.careInstructions'), description: deliverData.careOrders.slice(0, 2).map(txt).join(', '), status: deliverData.careOrdersComplete ? 'completed' : 'pending', path: '/case/deliver/instructions', state: { orderType: 'careOrders', items: deliverData.careOrders }, dueInfo: dueInfo('careOrders') })
    if (deliverData.labs.length > 0) list.push({ id: 'labs', type: 'labs', icon: FlaskConical, title: t('deliver.labWork'), description: deliverData.labs.slice(0, 2).map(txt).join(', '), status: deliverData.labsStatus || (deliverData.labsComplete ? 'completed' : 'pending'), path: '/case/deliver/further-testing', state: { testType: 'labs', items: deliverData.labs }, dueInfo: dueInfo('labs'), facility: deliverData.labsFacility })
    if (deliverData.imaging.length > 0) list.push({ id: 'imaging', type: 'imaging', icon: Scan, title: t('deliver.imagingStudies'), description: deliverData.imaging.slice(0, 2).map(txt).join(', '), status: deliverData.imagingStatus || (deliverData.imagingComplete ? 'completed' : 'pending'), path: '/case/deliver/further-testing', state: { testType: 'imaging', items: deliverData.imaging }, dueInfo: dueInfo('imaging'), facility: deliverData.imagingFacility })
    // v221.1.2: Diagnostic tests (audiogram, EEG, PFT, sleep study) — own bucket
    if (deliverData.diagnosticTests && deliverData.diagnosticTests.length > 0) list.push({ id: 'diagnosticTests', type: 'diagnosticTests', icon: ListChecks, title: t('deliver.diagnosticTests'), description: deliverData.diagnosticTests.slice(0, 2).map(txt).join(', '), status: deliverData.diagnosticTestsComplete ? 'completed' : 'pending', path: '/case/deliver/instructions', state: { orderType: 'diagnosticTests', items: deliverData.diagnosticTests }, dueInfo: dueInfo('diagnosticTests') })
    if (deliverData.referrals.length > 0) list.push({ id: 'referrals', type: 'referrals', icon: Send, title: t('deliver.referrals'), description: deliverData.referrals.slice(0, 2).map(txt).join(', '), status: deliverData.referralsComplete ? 'completed' : 'pending', path: '/case/deliver/instructions', state: { orderType: 'referrals', items: deliverData.referrals }, dueInfo: dueInfo('referrals') })
    // v221.1.2: DME — Equipment & supplies (patient-facing label)
    if (deliverData.dme && deliverData.dme.length > 0) list.push({ id: 'dme', type: 'dme', icon: Package, title: t('deliver.dme'), description: deliverData.dme.slice(0, 2).map(txt).join(', '), status: deliverData.dmeComplete ? 'completed' : 'pending', path: '/case/deliver/instructions', state: { orderType: 'dme', items: deliverData.dme }, dueInfo: dueInfo('dme') })
    // Legacy back-compat: render any items still in additionalOrders that haven't moved to diagnosticTests
    if (deliverData.additionalOrders.length > 0 && (!deliverData.diagnosticTests || deliverData.diagnosticTests.length === 0)) list.push({ id: 'additionalOrders', type: 'additionalOrders', icon: ListChecks, title: t('deliver.additionalOrders'), description: deliverData.additionalOrders.slice(0, 2).map(txt).join(', '), status: deliverData.additionalOrdersComplete ? 'completed' : 'pending', path: '/case/deliver/instructions', state: { orderType: 'additionalOrders', items: deliverData.additionalOrders }, dueInfo: dueInfo('additionalOrders') })
    if (deliverData.procedures.length > 0) list.push({ id: 'procedures', type: 'procedures', icon: Syringe, title: t('deliver.procedures'), description: deliverData.procedures.slice(0, 2).map(txt).join(', '), status: deliverData.proceduresComplete ? 'completed' : 'pending', path: '/case/deliver/instructions', state: { orderType: 'procedures', items: deliverData.procedures }, dueInfo: dueInfo('procedures') })
    if (deliverData.customOrders.length > 0) list.push({ id: 'customOrders', type: 'customOrders', icon: FileText, title: t('deliver.customOrders'), description: deliverData.customOrders.slice(0, 2).map(txt).join(', '), status: deliverData.customOrdersComplete ? 'completed' : 'pending', path: '/case/deliver/instructions', state: { orderType: 'customOrders', items: deliverData.customOrders }, dueInfo: dueInfo('custom') })
    if (deliverData.otherRequirements.trim()) list.push({ id: 'other', type: 'other', icon: AlertCircle, title: t('deliver.additionalInstructions'), description: deliverData.otherRequirements.substring(0, 60), status: deliverData.otherComplete ? 'completed' : 'pending', path: '/case/deliver/instructions', state: { orderType: 'other', text: deliverData.otherRequirements }, dueInfo: dueInfo('other') })

    // ★★★ v242.116.0 · CARECARD-MDPANEL-1 — the checklist, and the ids that make a tick
    //   here and a tick on InstructionsScreen THE SAME FACT. The id scheme
    //   `<type>-<idx>` is COPIED from InstructionsScreen's instructionsData memo, not
    //   re-derived: it indexes `ordersPlaced[type]`, which is the same array this list
    //   was built from, so index N means the same order on both screens.
    //   ★★ IF THAT SCHEME EVER MOVES, IT MOVES IN BOTH FILES IN ONE COMMIT — two writers
    //     of one key that disagree about what an id means is a chart disagreeing with itself.
    // ★ TEST 2.14: `checkedIds` for the order types InstructionsScreen tracks
    //   (everything except labs/imaging, which stay on FurtherTestingScreen's
    //   own flat field — out of this step's scope) now reads the same
    //   per-encounter `careTicks` store InstructionsScreen writes, via the
    //   SAME position-id scheme (`legacyIds`) this card has always read — so
    //   this read-only progress display can never disagree with what the
    //   patient actually ticked, and a new encounter's orders show 0 ticked
    //   here too, not whatever a past encounter with the same wording left.
    const enc = `e${currentEncounterNumber(currentCase)}`
    const today = dayKey()
    const careTicksStore = deliver?.careTicks
    const TRACKED_TYPES = new Set(['careOrders', 'diagnosticTests', 'dme', 'additionalOrders', 'procedures', 'customOrders', 'referrals'])
    // ★★ s101 ORDER-CONSIDER-1 (Dr. Rami, 2026-10-12): "If the order is under consideration it does
    //   not get ANY card ... Deliver is just for things that need to be done before the next visit."
    //   New cases never send one (the panel keeps them out of ordersPlaced); a LEGACY per-category
    //   'under-consideration' due date still on a live case hides that card here.
    return list.filter(task => task.dueInfo?.tone !== 'under-consideration').map(task => {
      const raw = Array.isArray(task.state?.items) ? task.state.items : []
      const checkedIds = (raw.length > 0 && TRACKED_TYPES.has(task.type))
        ? legacyIds(task.type, raw, careTicksStore, enc, today)
        : (deliver[`${task.type}CheckedItems`] || [])
      return {
        ...task,
        items: raw.map((item, idx) => ({ id: `${task.type}-${idx}`, text: txt(item) })),
        checkedIds,
        pill: task.dueInfo?.tone || null,
        pillLabel: task.dueInfo?.text || null,
      }
    })
  }, [deliverData, deliver, currentCase])

  // ==========================================
  // APPOINTMENT DATA
  // ==========================================
  const appointment = useMemo(() => {
    if (!currentCase) return null
    const encNum = currentEncounterNumber(currentCase)

    const formatAppt = (dateStr, timeStr, extra = {}) => {
      if (!dateStr) return null
      const dateObj = parseDate(dateStr)
      if (!dateObj) return null
      const today = new Date(); today.setHours(0, 0, 0, 0)
      if (dateObj < today) return null
      return { date: formatDate(dateStr, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }), time: timeStr ? formatAppointmentTime(timeStr) : null, rawDate: dateStr, rawTime: timeStr, ...extra }
    }

    // v171.16.0: Helper — reject appointments from previous encounters
    const isStaleAppt = (apt) => {
      if (!apt) return false
      // If the appointment has an encounter_number and it doesn't match current, it's stale
      if (apt.encounter_number && apt.encounter_number !== encNum) return true
      // If the appointment is completed/cancelled/frozen, it belongs to a finished encounter
      const staleStatuses = ['completed', 'cancelled', 'frozen']
      if (staleStatuses.includes(apt.status)) return true
      return false
    }

    const apiAppt = currentCase.followUpAppointment
    const followUp = currentCase.followUp || {}
    const followUpHasAppt = followUp.appointmentScheduled && followUp.scheduledDate
    const apiHasAppt = apiAppt && (apiAppt.date || apiAppt.scheduled_date)

    // v171.16.0: Skip stale API appointment from a previous encounter
    if (apiHasAppt && isStaleAppt(apiAppt)) {
      console.log('[Deliver] Skipping stale followUpAppointment enc=', apiAppt.encounter_number, 'status=', apiAppt.status, 'current=', encNum)
      // Don't use apiAppt — fall through to appointments array or return null
    } else {
      let useFollowUp = false
      if (followUpHasAppt && apiHasAppt) {
        const fuDate = followUp.scheduledDate
        const apiDate = apiAppt.date || apiAppt.scheduled_date
        if (fuDate !== apiDate || (followUp.scheduledTime && followUp.scheduledTime !== (apiAppt.time || apiAppt.scheduled_time))) {
          const fuModified = new Date(followUp.modifiedAt || 0).getTime()
          const apiModified = new Date(apiAppt.updated_at || apiAppt.modified_at || 0).getTime()
          useFollowUp = fuModified >= apiModified
        }
      } else if (followUpHasAppt && !apiHasAppt) {
        useFollowUp = true
      }

      if (useFollowUp || (followUpHasAppt && !apiHasAppt)) {
        return formatAppt(followUp.scheduledDate, followUp.scheduledTime, {
          id: apiAppt?.id, visitType: followUp.visitType || apiAppt?.type, scheduledBy: followUp.scheduledBy, scheduledByName: followUp.scheduledByName,
          status: followUp.appointmentStatus || followUp.status, doctorName: apiAppt?.doctor_name,
          // v222.4.8 — defensive: practice_address may be a JSONB dict / JSON-string
          practiceName: apiAppt?.practice_name, practiceAddress: formatAddress(apiAppt?.practice_address),
          practiceCity: apiAppt?.practice_city, practiceState: apiAppt?.practice_state, practiceZip: apiAppt?.practice_zip,
          practicePhone: apiAppt?.practice_phone, join: videoJoinTarget(apiAppt),
        })
      }

      if (apiHasAppt) {
        return formatAppt(apiAppt.date || apiAppt.scheduled_date, apiAppt.time || apiAppt.scheduled_time, {
          id: apiAppt.id, visitType: apiAppt.type || apiAppt.appointment_type, doctorName: apiAppt.doctor_name,
          // v222.4.8 — defensive: practice_address may be a JSONB dict / JSON-string
          practiceName: apiAppt.practice_name, practiceAddress: formatAddress(apiAppt.practice_address),
          practiceCity: apiAppt.practice_city, practiceState: apiAppt.practice_state, practiceZip: apiAppt.practice_zip,
          practicePhone: apiAppt.practice_phone, join: videoJoinTarget(apiAppt),
          scheduledBy: apiAppt.scheduled_by, status: apiAppt.status,
        })
      }
    }

    // v171.16.0: Filter appointments array by current encounter + skip stale
    const appointments = currentCase.appointments || []
    const followUpAppt = appointments.find(a => {
      if (isStaleAppt(a)) return false
      if (isOutsideAppointment(a)) return false
      if (a.appointment_category) return ['follow_up', 'observation_checkin', 'reopened'].includes(a.appointment_category)
      const type = (a.type || '').toLowerCase()
      return type.includes('follow')
    })
    if (followUpAppt) {
      return formatAppt(followUpAppt.scheduledDate || followUpAppt.date, followUpAppt.scheduledTime || followUpAppt.time, {
        id: followUpAppt.id, visitType: followUpAppt.type, doctorName: followUpAppt.doctor_name,
        practiceName: followUpAppt.practice_name, join: videoJoinTarget(followUpAppt),
        // PROD-1004: the row status reaches the card (pending_reschedule reads Pending, not Confirmed).
        status: followUpAppt.status,
      })
    }

    const scheduled = currentCase.scheduledAppointment
    if (scheduled && (scheduled.type === 'follow-up' || scheduled.appointment_type === 'follow-up')) {
      return formatAppt(scheduled.scheduledDate || scheduled.date, scheduled.scheduledTime || scheduled.time)
    }

    return null
  }, [currentCase])

  // ==========================================
  // SYMPTOM QUESTIONS (dynamic per focus area)
  // ==========================================
  // v163.6.0: AI-powered symptom questions with static fallback
  const staticSymptomQuestions = useMemo(() => {
    const focusArea = (currentCase?.focusAreaName || '').toLowerCase()
    const base = [
      { key: 'overall', question: t('deliver.howFeelingOverall'), options: [t('deliver.muchBetter'), t('deliver.somewhatBetter'), t('deliver.aboutSame'), t('deliver.somewhatWorse'), t('deliver.muchWorse')], neg: [0, 1] },
      { key: 'new_symptoms', question: t('deliver.anyNewSymptoms'), options: [t('deliver.none'), t('deliver.minor'), t('deliver.yesNoticeable')], neg: [0] },
    ]
    if (focusArea.includes('congest') || focusArea.includes('sinus') || focusArea.includes('nose')) {
      base.push({ key: 'congestion', question: t('deliver.howIsCongestion'), options: [t('deliver.resolved'), t('deliver.muchBetter'), t('deliver.slightlyBetter'), t('deliver.same'), t('deliver.worse')], neg: [0, 1] })
      base.push({ key: 'breathing', question: t('deliver.anyDifficultyBreathing'), options: ['No', t('deliver.occasionally'), t('deliver.mostNights')], neg: [0] })
    } else if (focusArea.includes('ear') || focusArea.includes('hearing')) {
      base.push({ key: 'hearing', question: t('deliver.howIsHearing'), options: [t('deliver.normal'), t('deliver.improved'), t('deliver.same'), t('deliver.worse')], neg: [0, 1] })
    } else if (focusArea.includes('throat') || focusArea.includes('voice')) {
      base.push({ key: 'throat', question: t('deliver.howIsThroat'), options: [t('deliver.normal'), t('deliver.improved'), t('deliver.same'), t('deliver.worse')], neg: [0, 1] })
    } else if (focusArea.includes('skin') || focusArea.includes('rash')) {
      base.push({ key: 'skin', question: t('deliver.howDoesSkinLook'), options: [t('deliver.clearedUp'), t('deliver.muchBetter'), t('deliver.slightlyBetter'), t('deliver.same'), t('deliver.worse')], neg: [0, 1] })
    }
    return base
  }, [currentCase?.focusAreaName])

  const [symptomQuestions, setSymptomQuestions] = useState(staticSymptomQuestions)
  const [bridgeQuestionsLoading, setBridgeQuestionsLoading] = useState(false)
  const [bridgeQuestionsSource, setBridgeQuestionsSource] = useState('static') // 'ai' | 'static'
  const [medAiQuestions, setMedAiQuestions] = useState(null) // v164.0.0: AI medication review questions
  const bridgeFetchedRef = useRef(false)

  // v173.0.0: Unified follow-up questions state (replaces separate symptom + med modals)
  const [unifiedQuestions, setUnifiedQuestions] = useState(null) // AI-generated questions array
  const [unifiedQuestionsLoading, setUnifiedQuestionsLoading] = useState(false)
  const [unifiedQuestionsError, setUnifiedQuestionsError] = useState(null)
  const [unifiedAnswers, setUnifiedAnswers] = useState(deliver.unifiedAnswersData || {})
  const [unifiedDetails, setUnifiedDetails] = useState(deliver.unifiedDetailsData || {})
  const [useUnifiedFlow, setUseUnifiedFlow] = useState(false) // true when AI questions loaded successfully
  const [unifiedFlowFailed, setUnifiedFlowFailed] = useState(false) // true when AI failed — show legacy fallback
  const unifiedFetchedRef = useRef(false)

  // v174.0.0: Detect if this is a follow-up case that should attempt unified flow
  // Broadened check: status fields, followUp object, encounter number > 1, or deliverState
  const isFollowUpCase = !!(
    currentCase?.status === 'in_follow_up' || currentCase?.status === 'in_observation' ||
    currentCase?.status === 'follow_up' ||
    currentCase?.followUp?.disposition === 'follow_up' || currentCase?.followUp?.disposition === 'observation' ||
    currentCase?.followUp?.type === 'follow_up' || currentCase?.followUp?.type === 'observation' ||
    (currentCase?.currentEncounter > 1) ||
    (currentCase?.encounters?.length > 1)
  )
  // Show unified path if: already loaded, or is follow-up and hasn't failed yet
  const showUnifiedPath = useUnifiedFlow || (isFollowUpCase && !unifiedFlowFailed)

  // v173.0.0: Generate unified follow-up questions AFTER voice/text gate clears
  // Uses the patient's transcript + case data for personalized questions
  const generateUnifiedQuestions = async (transcript) => {
    if (unifiedFetchedRef.current || !currentCase?.id) return
    unifiedFetchedRef.current = true
    setUnifiedQuestionsLoading(true)
    setUnifiedQuestionsError(null)
    
    // v242.170.0 FU-REPORT-ENCOUNTER-GUESS-1 — the row the PATIENT is standing in,
    // not the last row in the array. This id is where the server reads and WRITES
    // the bridge-question cache (encounters.interval_data), so between a finalize
    // and the patient's acknowledgment it used to write onto the newly inserted
    // encounter while the report went to the pointer's. See utils/caseEncounters.js.
    const encounterId = currentEncounterId(currentCase)
    const bridgeType = bridgeTypeFor(currentCase, deliverData)
    
    try {
      console.log(`[Deliver] v173: Generating unified questions with transcript (${transcript?.length || 0} chars)`)
      const result = await aiAPI.generateBridgeQuestions(
        currentCase.id, encounterId, bridgeType, language, transcript || ''
      )
      
      if (result?.questions?.length >= 2) {
        setUnifiedQuestions(result.questions)
        setUseUnifiedFlow(true)
        setBridgeQuestionsSource('ai-unified')
        console.log(`[Deliver] v173: ✅ Unified questions loaded: ${result.questions.length} Qs`)
        
        // Cache the questions on deliver data
        saveDeliverData({ unifiedQuestionsData: result.questions, unifiedQuestionsSource: 'ai-unified' })
        
        // Cascade to questions step after a brief pause
        setTimeout(() => setStep(s => Math.max(s, 5)), 400)
        setTimeout(() => setStep(s => Math.max(s, 6)), 800)
      } else {
        console.log('[Deliver] v173: AI returned insufficient unified questions, falling back to separate modals')
        setUnifiedQuestionsError('insufficient')
        setUseUnifiedFlow(false)
        setUnifiedFlowFailed(true)
        // Fallback: advance to old step 4 (symptom modal card)
        setStep(s => Math.max(s, 4))
      }
    } catch (err) {
      console.warn('[Deliver] v173: Unified questions failed, falling back to separate modals:', err?.message)
      setUnifiedQuestionsError(err?.message || 'Failed')
      setUseUnifiedFlow(false)
      setUnifiedFlowFailed(true)
      // Fallback: advance to old step 4 (symptom modal card)
      setStep(s => Math.max(s, 4))
    } finally {
      setUnifiedQuestionsLoading(false)
    }
  }

  // Fetch AI bridge questions when the deliver screen mounts (for follow-up encounters)
  // v173.0.0: This is now the FALLBACK path — primary path is unified questions after voice
  useEffect(() => {
    if (bridgeFetchedRef.current || !currentCase?.id) return
    // v174.0.0: Use isFollowUpCase computed above
    if (!isFollowUpCase) {
      setSymptomQuestions(staticSymptomQuestions)
      return
    }
    bridgeFetchedRef.current = true
    setBridgeQuestionsLoading(true)
    
    // v242.170.0 FU-REPORT-ENCOUNTER-GUESS-1 — the row the PATIENT is standing in,
    // not the last row in the array. This id is where the server reads and WRITES
    // the bridge-question cache (encounters.interval_data), so between a finalize
    // and the patient's acknowledgment it used to write onto the newly inserted
    // encounter while the report went to the pointer's. See utils/caseEncounters.js.
    const encounterId = currentEncounterId(currentCase)
    const bridgeType = bridgeTypeFor(currentCase, deliverData)
    
    aiAPI.generateBridgeQuestions(currentCase.id, encounterId, bridgeType, language)
      .then(result => {
        if (result?.questions?.length >= 2) {
          // Adapt AI format to SymptomCheckModal format
          const adapted = result.questions.map((q, i) => ({
            key: q.id || `bq${i}`,
            question: q.text,
            options: q.type === 'free-text' ? ['(free text)'] : (q.options || []),
            neg: q.type === 'free-text' ? [] : [0, 1], // First 2 options are typically positive/negative
            type: q.type,
            clinicalRationale: q.clinicalRationale,
            context: q.context,
          }))
          setSymptomQuestions(adapted)
          setBridgeQuestionsSource('ai')
          console.log(`[Deliver] ✅ AI bridge questions loaded: ${adapted.length} Qs (${bridgeType})`)
        } else {
          console.log('[Deliver] AI returned insufficient questions, using static fallback')
          setSymptomQuestions(staticSymptomQuestions)
        }
      })
      .catch(err => {
        console.warn('[Deliver] AI bridge questions failed, using static fallback:', err?.message)
        setSymptomQuestions(staticSymptomQuestions)
      })
      .finally(() => setBridgeQuestionsLoading(false))

    // v164.0.0: Also fetch medication review AI questions in parallel
    // ★ PROD-1079: the column's medications only once the case has a signed visit (never md's draft).
    const hasMedications = (signedOrdersPlaced(currentCase).medications || []).length > 0 ||
      (currentCase?.deliver?.medications || []).length > 0
    if (hasMedications) {
      aiAPI.generateBridgeQuestions(currentCase.id, encounterId, 'medication_review', language)
        .then(result => {
          if (result?.questions?.length >= 1) {
            setMedAiQuestions(result.questions)
            console.log(`[Deliver] ✅ AI medication review questions loaded: ${result.questions.length} Qs`)
          }
        })
        .catch(err => console.warn('[Deliver] AI med review questions failed (non-blocking):', err?.message))
    }
  }, [currentCase?.id, currentCase?.status])

  // ==========================================
  // COMPUTED STATE
  // ==========================================
  const orderCompletedCount = orderTasks.filter(t => t.status === 'completed').length
  const hasMeds = (deliverData?.medications || []).length > 0
  const followUpPlan = deliverData?.followUpPlan
  // v125.0.7: Robust doctor data — fallback through sentToDoctorData, appointments,
  // case fields. ★★ v242.175.0: THIS BLOCK IS NOW A CALL. It lived inline here for
  //   fifty versions while ScheduleFollowupScreen — the screen this one opens —
  //   resolved the same doctor from one column and got it wrong on every follow-up.
  //   The chain is unchanged; it just has one home now (utils/caseDoctor.js).
  const doctorData = caseDoctor(currentCase)
  const hasAppointment = !!appointment
  // v242.170.0 — one owner for this question; see utils/caseEncounters.js.
  const encounterNumber = currentEncounterNumber(currentCase)
  const caseInfo = `${(currentCase?.specialtyName || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())} > ${currentCase?.focusAreaName || ''}`

  // Total tasks: voice + symptoms + (med review if meds) + order tasks
  // v173.0.0: In unified flow, questions replace both symptom + med review
  const totalTasks = useUnifiedFlow 
    ? 1 + 1 + orderTasks.length  // voice + unified questions + orders
    : 2 + (hasMeds ? 1 : 0) + orderTasks.length
  const completedTasks = useUnifiedFlow
    ? [voiceMemo, unifiedQuestions && Object.keys(unifiedAnswers).length > 0 ? true : null].filter(Boolean).length + orderCompletedCount
    : [voiceMemo, symptomSurvey, ...(hasMeds ? [medicationReview] : [])].filter(Boolean).length + orderCompletedCount
  const allTasksComplete = completedTasks >= totalTasks
  // v173.0.0: canSubmit — unified flow requires voice + at least 1 unified answer; legacy requires voice + symptoms
  const canSubmit = useUnifiedFlow 
    ? (voiceMemo || deliver.voiceSkipped) && unifiedQuestions && Object.keys(unifiedAnswers).length > 0
    : voiceMemo && symptomSurvey

  // Auto-advance step 6→7 when no meds (nothing to gate on)
  useEffect(() => {
    if (step === 6 && !hasMeds) {
      const t = setTimeout(() => setStep(7), 600)
      return () => clearTimeout(t)
    }
  }, [step, hasMeds])

  // Deliver state (which card to show)
  const getDeliverState = () => {
    if (followUpPlan === 'observation') return 'observation'
    if (followUpPlan === 'completed' || followUpPlan === 'complete' || followUpPlan === 'pcp') return 'complete'
    if (followUpPlan === 'referral' || followUpPlan === 'refer') return 'referral'
    if (allTasksComplete && hasAppointment) return 'all-done'
    if (hasAppointment) return 'scheduled'
    return 'follow-up'
  }
  const deliverState = getDeliverState()

  // Persist deliverState
  useEffect(() => {
    if (currentCase && deliverState && currentCase.deliverState !== deliverState) {
      updateAndSaveCase({ deliverState })
    }
  }, [deliverState])

  // Chat messages per state
  const getChatMessage = () => {
    switch (deliverState) {
      // ★★★ v242.117.0 · DELIVER-ASKORELLA-1 — "we're recommending" was Orella
      //   co-signing a clinical plan. THE RECOMMENDATION IS THE DOCTOR'S; Orella
      //   relays it. Same rule that took recommendations off the result cards.
      //   ★★ AND THESE TWO BRANCHES WERE HARDCODED ENGLISH while every sibling used
      //     t() — the screen was untranslatable in exactly the two states a
      //     follow-up patient is in. Both are keys now.
      case 'follow-up': return totalTasks > 0
        ? t('deliver.planFromDoctor')
        : t('deliver.doctorWillFollowUpDirectly')
      case 'scheduled':
        return t('deliver.followUpScheduledTasksMsg')
      case 'all-done':
        return t('deliver.allTasksDoneMsg')
      case 'observation': return t('deliver.observationMonitorMsg')
      case 'complete': return t('deliver.caseCompleteMsg')
      case 'referral': return t('deliver.referralMsg')
      default: return t('deliver.hereWhereThingsStand')
    }
  }

  // ★ "your plan" only where there IS one. In observation / complete / referral the
  //   chip is the generic Ask Orella, because naming a plan the patient does not have
  //   is a label that lies about what it opens.
  const hasPlanToWorkThrough = (deliverState === 'follow-up' || deliverState === 'scheduled') && totalTasks > 0

  const getChatTint = () => {
    switch (deliverState) {
      case 'all-done': case 'complete': return { bg: '#F0FDF4', border: '#BBF7D0' }
      case 'observation': return null // v124.6.0: standard blue, not amber
      case 'referral': return { bg: '#EFF6FF', border: '#BFDBFE' }
      default: return null
    }
  }

  const getStateIntro = () => {
    switch (deliverState) {
      case 'follow-up': return t('deliver.followUpRecommendation')
      case 'scheduled': return t('deliver.appointmentDetails')
      case 'all-done': return t('deliver.everythingInOrder')
      case 'observation': return t('deliver.whatWeAreMonitoring')
      case 'complete': return t('deliver.careWrappedUp')
      case 'referral': return t('deliver.referralToSpecialist')
      default: return t('deliver.hereWhereThingsStandShort')
    }
  }

  // ==========================================
  // v120.9.7 REF-1: START REFERRAL CASE
  // ==========================================
  const [isReopening, setIsReopening] = useState(false)
  const [reopenMode, setReopenMode] = useState(false)
  const [reopenStep, setReopenStep] = useState(0) // 0=idle, 1=schedule, 2=pharmacy, 3=insurance, 4=allergies/meds, 5=profile+submit
  const [reopenDate, setReopenDate] = useState('')
  const [reopenTime, setReopenTime] = useState('')
  const [reopenVisitType, setReopenVisitType] = useState('in-person')
  const [reopenSending, setReopenSending] = useState(false)
  const [reopenSlots, setReopenSlots] = useState(null) // Doctor's available slots for reopen scheduling
  // D7 (PD7-2): where each reopen slot is (utils/bookableSlots.js slotPlacesOf); null for an
  //   API without `locations`, and then the reopen picker is what it was before D7.
  const [reopenPlaces, setReopenPlaces] = useState(null)
  // TEST 1.35 — WHY there is nothing to pick (utils/bookableSlots.js); null when slots exist
  const [reopenSlotsReason, setReopenSlotsReason] = useState(null)
  const [reopenSlotsLoading, setReopenSlotsLoading] = useState(false)
  const [reopenSlotsAttempt, setReopenSlotsAttempt] = useState(0)
  // ★★ P7 A8/P1 (TEST 2.10; Alex Q6, Q7) — the follow-up request's own words and up to 5
  //   photos. Q7: photos upload WHEN PICKED (sequentially, through the same
  //   uploadToStorageResult the Detect screen uses), not on submit — so an abandoned
  //   request can leave up to 5 unused files in the case's storage (accepted). Each entry
  //   is { id, name, path, uploading, error }; `path` is the exact key
  //   patient_create_upload_url mints, echoed back by /cases/<id>/reopen as
  //   followUp.reopenPhotos. Submit is blocked while any entry is uploading or failed —
  //   never sent silently (Q7).
  const [reopenNotes, setReopenNotes] = useState('')
  const [reopenPhotos, setReopenPhotos] = useState([])

  // v218.0.7: Deep-link entry. CaseTimeline's "Reopen case" navigates here with
  // state:{reopen:true}. Auto-enter the reopen flow on mount — mirrors exactly
  // what the CareCompleteCard "Request Follow-Up" button does
  // (setReopenMode(true); setReopenStep(0)). Fully additive and isolated: if the
  // state isn't passed, this no-ops and DeliverScreen behaves exactly as before.
  // Fires once; only when the case is genuinely in a closed/finalized state.
  const reopenDeepLinkRef = useRef(false)
  useEffect(() => {
    if (reopenDeepLinkRef.current) return
    if (!location.state?.reopen) return
    const closed = ['completed', 'closed'].includes(currentCase?.status) ||
      !!(currentCase?.avsContent || currentCase?.afterVisitSummary)
    if (!closed) return
    reopenDeepLinkRef.current = true
    setReopenMode(true)
    // ★★ L10/P7 P2 (TEST 2.09) — a profile subpage (insurance/allergies/medications) that
    //   sent the patient here to finish the in-progress reopen draft hands it back through
    //   `location.state.reopenDraft`. Router state only (WKWebView recycles sessionStorage:
    //   check-auth-storage). Photo keys survive the trip as plain strings — P1 already
    //   uploaded them when picked, so nothing here re-uploads.
    const draft = location.state?.reopenDraft
    if (draft) {
      setReopenStep(draft.step || 1)
      if (draft.date) setReopenDate(draft.date)
      if (draft.time) setReopenTime(draft.time)
      if (draft.visitType) setReopenVisitType(draft.visitType)
      if (draft.notes) setReopenNotes(draft.notes)
      if (Array.isArray(draft.photoKeys) && draft.photoKeys.length) {
        setReopenPhotos(draft.photoKeys.map((path, i) => ({ id: `restored-${i}-${path}`, name: path.split('/').pop(), path, uploading: false, error: null })))
      }
    } else {
      setReopenStep(0)
    }
  }, [location.state, currentCase?.status])

  // ★★ P7 A8/P1 (TEST 2.10; Alex Q7) — each picked photo uploads immediately, sequentially,
  //   through the same uploadToStorageResult the Detect screen uses. A failed upload is
  //   shown inline and blocks submit (handleReopenSubmit below); it is never sent silently.
  const reopenPhotoKeys = reopenPhotos.filter((p) => p.path && !p.error && !p.uploading).map((p) => p.path)
  const handleReopenPhotoPick = async (e) => {
    const files = Array.from(e.target.files || [])
    e.target.value = ''
    if (files.length === 0 || !currentCase?.id) return
    const room = 5 - reopenPhotos.filter((p) => !p.error).length
    const toAdd = files.slice(0, Math.max(0, room))
    for (const file of toAdd) {
      const id = `${Date.now()}_${Math.random().toString(36).slice(2)}`
      setReopenPhotos((prev) => [...prev, { id, name: file.name, path: null, uploading: true, error: null }])
      try {
        const { uploadToStorageResult } = await import('../../services/storage')
        const upRes = await uploadToStorageResult(currentCase.id, file.name, file, file.type)
        setReopenPhotos((prev) => prev.map((p) => (p.id === id
          ? (upRes.ok ? { ...p, path: upRes.path, uploading: false } : { ...p, uploading: false, error: true })
          : p)))
      } catch (err) {
        console.warn('[Deliver] Follow-up photo upload failed:', err)
        setReopenPhotos((prev) => prev.map((p) => (p.id === id ? { ...p, uploading: false, error: true } : p)))
      }
    }
  }
  const removeReopenPhoto = (id) => setReopenPhotos((prev) => prev.filter((p) => p.id !== id))
  const reopenDraftFor = () => ({ step: reopenStep, date: reopenDate, time: reopenTime, visitType: reopenVisitType, notes: reopenNotes, photoKeys: reopenPhotoKeys })

  // Fetch doctor availability when reopen mode activates.
  // ★★ TEST 1.35 / TEST 2.08 — only real slots. enabled:false, "on with no days", nothing
  //   open and a failed read each leave reopenSlots EMPTY and say which it was; the
  //   screen shows NoSlotsCard instead of the 14 generated weekdays and the static
  //   8:00 AM–4:30 PM list it used to offer.
  useEffect(() => {
    if (!reopenMode) return
    const doctorId = currentCase?.sentToDoctor || currentCase?.doctor?.id || currentCase?.doctor_id
    let cancelled = false
    const loadSlots = async () => {
      setReopenSlotsLoading(true)
      const { slots, reason, places } = await bookableSlots(doctorsAPI, doctorId, 21, 'follow_up')
      if (cancelled) return
      if (reason) console.log('[Deliver] Reopen: no slots to offer —', reason)
      setReopenSlots(slots)
      setReopenPlaces(places || null)
      setReopenSlotsReason(reason)
      setReopenSlotsLoading(false)
    }
    loadSlots()
    return () => { cancelled = true }
  }, [reopenMode, currentCase?.sentToDoctor, currentCase?.doctor?.id, currentCase?.doctor_id, reopenSlotsAttempt])
  
  // v124.0.0: Observation check-in bridge flow states
  const [checkInMode, setCheckInMode] = useState(false) // true = check-in flow active
  const [checkInStep, setCheckInStep] = useState(0)     // 0=idle, 1=q1, 2=q2, 3=complete or bridge
  const [checkInQ1, setCheckInQ1] = useState(null)      // null | true (symptoms) | false (nothing)
  const [checkInQ2, setCheckInQ2] = useState(null)      // null | true (concerning) | false (nothing)
  const [showObsCheckInModal, setShowObsCheckInModal] = useState(false) // v164.0.0: AI observation check-in modal
  const [obsCheckInQuestions, setObsCheckInQuestions] = useState(null)   // v164.0.0: AI-generated observation questions
  const [obsCheckInLoading, setObsCheckInLoading] = useState(false)
  const [reportMode, setReportMode] = useState(false)   // true = positive finding bridge (voice + schedule)
  const [reportStep, setReportStep] = useState(0)       // 0=idle, 1=voice, 2=schedule, 3=send
  const [reportDate, setReportDate] = useState('')
  const [reportTime, setReportTime] = useState('')
  const [reportVisitType, setReportVisitType] = useState('in-person')
  const [reportSending, setReportSending] = useState(false)
  const [reportVoiceMemo, setReportVoiceMemo] = useState(null)
  const [reportTranscript, setReportTranscript] = useState('')
  // ★★ D-e (Alex) — the report bridge follows D-b too: it used to offer a free date input
  //   and a static 9:00 AM–5:00 PM list, so a patient with a positive finding could
  //   "request" a time the physician never opened. It reads the same real slots as the
  //   reopen flow and, without them, says to contact the office.
  const [reportSlots, setReportSlots] = useState(null)
  const [reportSlotsReason, setReportSlotsReason] = useState(null)
  const [reportSlotsLoading, setReportSlotsLoading] = useState(false)
  const [reportSlotsAttempt, setReportSlotsAttempt] = useState(0)
  // D7 (PD7-2 r1, review B-S2): where each report-a-concern follow-up slot is, as for the reopen
  //   picker (utils/bookableSlots.js slotPlacesOf); null for an API without `locations`, and then the
  //   picker is what it was before D7.
  const [reportPlaces, setReportPlaces] = useState(null)
  // ★★★ D-h (round 3, H1) — the concern is ALWAYS sent; only the appointment needs hours.
  //   Without real hours (reasonIsNoHours) the patient still reaches Send, with no slot,
  //   and orella-api records the finding and answers 409 doctor_has_no_hours with
  //   findingRecorded:true. That answer is the SENT state below, not an error, and Send
  //   is gone afterwards so the same concern is not recorded twice.
  //   Round 4 (L1): the state holds the refusal CODE the concern was recorded under
  //   (doctor_has_no_hours or no_positive_outcome), false until then, so the sent state
  //   says the right follow-up line (concernSentMessageKey).
  //   Round 5 (B1): the same holds for none-open and no-doctor (reasonAllowsSendWithoutSlot):
  //   the report goes without a slot and orella-api answers 409 no_open_slot with
  //   findingRecorded:true. Only a failed read (fetch-failed) keeps Continue away.
  const [reportConcernSent, setReportConcernSent] = useState(false)
  useEffect(() => {
    if (!reportMode) return
    const doctorId = currentCase?.sentToDoctor || currentCase?.doctor?.id || currentCase?.doctor_id
    let cancelled = false
    const loadSlots = async () => {
      setReportSlotsLoading(true)
      const { slots, reason, places } = await bookableSlots(doctorsAPI, doctorId, 21, 'follow_up')
      if (cancelled) return
      if (reason) console.log('[Deliver] Report bridge: no slots to offer —', reason)
      setReportSlots(slots)
      setReportPlaces(places || null)
      setReportSlotsReason(reason)
      setReportSlotsLoading(false)
    }
    loadSlots()
    return () => { cancelled = true }
  }, [reportMode, currentCase?.sentToDoctor, currentCase?.doctor?.id, currentCase?.doctor_id, reportSlotsAttempt])
  
  // Auto-advance check-in steps
  useEffect(() => {
    if (checkInMode && checkInStep === 0) {
      const t = setTimeout(() => setCheckInStep(1), 300)
      return () => clearTimeout(t)
    }
  }, [checkInMode, checkInStep])
  
  // Auto-advance report steps
  useEffect(() => {
    if (reportMode && reportStep === 0) {
      const t = setTimeout(() => setReportStep(1), 300)
      return () => clearTimeout(t)
    }
  }, [reportMode, reportStep])
  
  // v124.0.0: Handle check-in submit (no/no path — negative check-in)
  const handleCheckInSubmit = async () => {
    try {
      // v242.170.0 — was `|| 2`: a typed constant wearing a default. A case with no
      // pointer does not have an encounter 2 because someone wrote a 2; the owner
      // falls back to the highest-numbered row that actually exists.
      const encNum = currentEncounterNumber(currentCase)
      await encountersAPI.submitCheckUp(currentCase.id, encNum, {
        notes: t('deliver.noChangesReported'),
        reportedChange: false,
      })
      console.log('[Deliver] Check-in submitted (negative)')
      setCheckInStep(3) // show complete
    } catch (err) {
      console.error('[Deliver] Check-in submit failed:', err)
    }
  }

  // v164.0.0: AI-powered observation check-in
  const handleObsCheckInTap = async () => {
    setObsCheckInLoading(true)
    try {
      const encounterId = (currentCase?.encounters || []).find(e => e.category === 'observation')?.id || null
      const result = await aiAPI.generateBridgeQuestions(currentCase.id, encounterId, 'observation_checkin', language)
      if (result?.questions?.length >= 2) {
        const adapted = result.questions.map((q, i) => ({
          key: q.id || `obq${i}`,
          question: q.text,
          options: q.type === 'free-text' ? ['(free text)'] : (q.options || []),
          neg: q.type === 'free-text' ? [] : [0, 1],
          type: q.type,
          clinicalRationale: q.clinicalRationale,
          context: q.context,
        }))
        // Add a final "report a change" escape question
        adapted.push({
          key: 'obs_escalation',
          // ★★ HARDCODED ENGLISH UNTIL v242.190.0. It reaches the patient as a PROP, and
          //   scripts/lib/jsxText.mjs matches JSX CHILDREN — text between '>' and '<' — so
          //   the app-wide ratchet pinned at ZERO was structurally unable to count it.
          question: t('deliver.obsEscalationQuestion'),
          options: [t('deliver.noEverythingStable'), t('deliver.yesNewSymptoms')],
          neg: [0],
          type: 'single-select',
        })
        setObsCheckInQuestions(adapted)
        setShowObsCheckInModal(true)
        console.log(`[Deliver] ✅ AI observation check-in questions loaded: ${adapted.length} Qs`)
      } else {
        // Fallback to old inline flow
        console.log('[Deliver] AI returned insufficient obs questions, using inline fallback')
        setCheckInMode(true)
        setCheckInStep(0)
      }
    } catch (err) {
      console.warn('[Deliver] AI obs check-in failed, using inline fallback:', err?.message)
      setCheckInMode(true)
      setCheckInStep(0)
    } finally {
      setObsCheckInLoading(false)
    }
  }

  // v226.9.4 (G4): Auto-open the observation check-in flow when deep-linked from a
  // notification (CaseHome forwards here with state.openObsCheckIn). Fires once.
  const obsCheckInDeepLinkRef = useRef(false)
  useEffect(() => {
    if (obsCheckInDeepLinkRef.current) return
    if (!location.state?.openObsCheckIn) return
    if (!currentCase?.id) return
    const isObs = isObservationCase(currentCase, deliverData)
    if (!isObs) return
    obsCheckInDeepLinkRef.current = true
    handleObsCheckInTap()
  }, [location.state, currentCase?.id, currentCase?.status])

  const handleObsCheckInComplete = async (data) => {
    setShowObsCheckInModal(false)
    // Check if patient escalated (selected "Yes — I have new symptoms")
    // ★★★ WAS: `data.obs_escalation === t('deliver.yesNewSymptoms')` — an equality test on
    //   TRANSLATED DISPLAY TEXT. It happens to work because both sides come from the same
    //   `t()` call in the same session, and it breaks the moment a locale string is edited,
    //   or the patient's language changes between building the question and answering it.
    //   The modal now reports whether the question's own author marked the chosen answer
    //   reassuring, which is language-independent and is the same fact the server reads.
    //   The string form is kept as a fallback for a modal build that predates `structured`.
    const escStructured = data?.structured?.obs_escalation
    const escalated = escStructured
      ? escStructured.reassuring === false
      : data.obs_escalation === t('deliver.yesNewSymptoms')
    if (escalated) {
      setReportMode(true)
      setReportStep(0)
      return
    }
    // Submit check-in with AI answers
    try {
      // v242.170.0 — was `|| 2`: a typed constant wearing a default. A case with no
      // pointer does not have an encounter 2 because someone wrote a 2; the owner
      // falls back to the highest-numbered row that actually exists.
      const encNum = currentEncounterNumber(currentCase)
      await encountersAPI.submitCheckUp(currentCase.id, encNum, {
        notes: JSON.stringify(data),
        reportedChange: false,
        _source: 'ai',
        _generatedAt: new Date().toISOString(),
        aiAnswers: data,
        // D332(f): the locale-independent half. The server converts on this, never on prose.
        structured: data?.structured || {},
      })
      console.log('[Deliver] AI observation check-up submitted')
      setCheckInMode(true)
      setCheckInStep(3) // show complete state
    } catch (err) {
      // ★★★ THIS CATCH USED TO SWALLOW THE FAILURE AND FALL THROUGH TO THE COMPLETE
      //   STATE ABOVE, so a 405 closed the sheet as though the answer had been saved.
      //   A check-up that did not reach the server must not tell the patient it did:
      //   the step stays where it was, and the caller sees the throw.
      // ★★★ THIS CATCH USED TO SWALLOW THE FAILURE AND FALL THROUGH TO THE COMPLETE
      //   STATE ABOVE, so a 405 closed the sheet as though the answer had been saved —
      //   which is why `checkIns` had five readers and no data for the life of the
      //   feature: it always LOOKED like it worked.
      // ★★ A `throw` here would be an unhandled rejection: this function is the modal's
      //   `onSubmit` (:3118). The sibling catch twenty lines up already has the right
      //   shape for "it did not work" — put the patient back at the start rather than
      //   showing them a completion that did not happen. No dialog: `check-native-dialogs`
      //   is a RATCHET and an added alert raises a count that is only allowed to fall.
      console.error('[Deliver] AI obs check-up submit failed:', err)
      setCheckInMode(true)
      setCheckInStep(0)
    }
  }
  
  // v124.0.0: Handle report finding submit (yes path — positive finding → follow-up)
  const handleReportFindingSubmit = async () => {
    if (reportSending || reportConcernSent || !currentCase?.id) return
    setReportSending(true)
    try {
      // v242.170.0 — was `|| 2`: a typed constant wearing a default. A case with no
      // pointer does not have an encounter 2 because someone wrote a 2; the owner
      // falls back to the highest-numbered row that actually exists.
      const encNum = currentEncounterNumber(currentCase)
      const result = await encountersAPI.reportFinding(currentCase.id, encNum, {
        finding: reportTranscript || t('deliver.patientReportedChange'),
        notes: reportTranscript || '',
        // P1 round 7 (R6-J2): without a time there is no slot -- send neither half, so a
        // date kept after a slot conflict never turns 'no open slot' into a 400.
        requestedDate: reportTime ? reportDate : '',
        // TEST 2.12 — 'HH:MM' (24h) in every payload; the API stores what it is sent
        requestedTime: reportTime ? toHHMM(reportTime) : null,
      })
      
      // PROD-903: flags and the id only — the answer carries the patient's finding text and notes.
      console.log('[Deliver] Observation finding reported (bridged):', { bridged: !!result?.bridged, appointmentId: result?.appointmentId ?? null })
      
      // v125.2.0: Bridge pattern — N+1 stays same encounter, category shifts
      // encounter_phase is diagnose_pending (awaiting MD), not deliver_active
      try {
        // v125.2.0: Also optimistically add the new pending appointment to the appointments array
        // so CaseHome's currentEncounterAppointment lookup finds it immediately
        const newPendingAppointment = {
          id: result.appointmentId || `pending-${Date.now()}`,
          case_id: currentCase.id,
          encounter_number: encNum,
          encounterNumber: encNum,
          appointment_category: 'follow_up',
          status: 'pending',
          date: reportDate,
          time: toHHMM(reportTime),
          type: reportVisitType === 'telehealth' ? 'video' : 'in_person',
          appointment_type: 'follow-up',
          patient_requested: true,
        }
        const existingAppointments = currentCase.appointments || []
        
        updateAndSaveCase({
          status: 'in_follow_up',
          encounterPhase: 'diagnose_pending',  // v125.2.0: Pending follow-up, not deliver_active
          currentEncounter: encNum,  // v125: Same encounter (bridged, not N+2)
          // ★★★ PROD-192 — NO appointmentRequest HERE. This PUT used to carry
          //   result.appointmentRequest or, failing that, a request the client made up
          //   (status 'pending', observationFinding). The appointment-request route
          //   WRITES appointment_request itself; the client only mirrors it (below).
          appointments: [...existingAppointments, newPendingAppointment],
          followUp: {
            ...currentCase.followUp,
            type: 'in_follow_up',
            observationBridged: true,
            appointmentStatus: 'pending',
            observationFinding: true,
          },
        })
        console.log('[Deliver] Store updated: observation bridged → follow-up pending (enc', encNum, ')')
      } catch (optErr) {
        console.warn('[Deliver] Optimistic update failed:', optErr)
      }

      // PROD-192 — LOCAL mirror only (updateCase never calls the API): the server's
      // own appointment_request, read back; if that read fails, the route's own
      // response. Nothing the server did not produce.
      try {
        const serverRequest = await readServerAppointmentRequest(currentCase.id)
        if (serverRequest || result?.appointmentRequest) updateCase({ appointmentRequest: serverRequest || result.appointmentRequest })
      } catch (readErr) {
        console.warn('[Deliver] appointment_request read-back failed (non-fatal):', readErr?.message || readErr)
        if (result?.appointmentRequest) updateCase({ appointmentRequest: result.appointmentRequest })
      }
      
      // Background refresh
      try {
        const { useCaseStore } = await import('../../stores/index')
        await useCaseStore.getState().loadCases()
      } catch (e) { /* non-blocking */ }
      
      navigate('/case/home')
    } catch (err) {
      console.error('[Deliver] Report finding failed:', err)
      // ★★★ D-h (round 3, H1) — FIRST: 409 doctor_has_no_hours WITH findingRecorded:true
      //   is success for the concern. The physician was notified; only the appointment
      //   was refused. No optimistic follow-up, no appointment row: the case stays in
      //   observation on the server, so it is read again rather than guessed at.
      //   Round 4 (L1): 400 no_positive_outcome WITH findingRecorded:true is the same
      //   success (the care plan has no follow-up to bridge to). The code is kept so the
      //   sent state reads the right line.
      //   Round 5 (B1, B3): 409 no_open_slot and 500 appointment_not_created WITH
      //   findingRecorded:true are the same success (CONCERN_RECORDED_CODES); the sent
      //   state says the office will contact the patient to schedule, and Send is gone.
      if (concernRecordedWithoutAppointment(err)) {
        setReportConcernSent(err.code)
        try { await useCaseStore.getState().loadCases() } catch (e) { /* non-blocking */ }
        return
      }
      // Round 5 (B2): the slot was taken and NOTHING was written (the API checks the slot
      //   before any seal), so the observation is still open. Keep the picker, clear the
      //   taken time, go back to it, and read the open times again.
      if (isSlotConflict(err)) {
        alert(t('errors2.timeSlotBooked'))
        setReportTime('')
        setReportStep(2)
        setReportSlotsAttempt((n) => n + 1)
        return
      }
      // doctor_has_no_hours / no_open_slot WITHOUT findingRecorded:true: nothing reached
      //   the physician.
      if (err?.code === 'doctor_has_no_hours' || err?.code === 'no_open_slot') {
        alert(`${t('errors2.failedToSendReport')} ${t('appointment.contactOfficeToArrange')}`)
        return
      }
      // Round 4 (L1): no_positive_outcome WITHOUT findingRecorded:true is the same failure;
      //   it is not "your appointment could not be created" (appointmentCreated:false).
      if (err?.code === 'no_positive_outcome') {
        alert(t('errors2.failedToSendReport'))
        return
      }
      // TEST 1.35 (D-c) — doctor_has_no_hours and the other contract codes first: a
      //   no-hours refusal is not "that time was just booked".
      const refusalKey = appointmentErrorKey(err)
      if (refusalKey) {
        alert(t(refusalKey))
      } else if (err.status === 409 || err.conflict) {
        // v140.0: Handle scheduling conflicts
        alert(t('errors2.timeSlotBooked'))
      } else {
        alert(t('errors2.failedToSendReport'))
      }
    } finally {
      setReportSending(false)
    }
  }
  
  // ── TEST 2.06: the Care Complete card's AVS download ──────────────────
  // ONE unwrap for both CareCompleteCard sites' `avsContent` prop and their
  // visibility test — was two independent `deliverData?.encounterAvs ||
  // currentCase?.avsContent || currentCase?.afterVisitSummary` reads, one of
  // which (the visibility test) never fell through to `afterVisitSummary`.
  // The finalize envelope this can carry — `{consultNote, avsContent,
  // billingCodes, cptCodes, ...}` — reaches the patient app (P4 Q9, filed);
  // unwrapping to `avsContent` only keeps a billing code out of the card and
  // the PDF. Mirrors recordsPackage.js's `avsTextFrom`, duplicated (not
  // imported) so this screen's bundle never pulls in jsPDF just to render a
  // card — only the download handler below does that, and only on click.
  // PROD-134 gamma: the stored AVS of the active encounter (cases.avs) through the one reader,
  // else the encounter's own text, else today's case-level chain.
  const deliverAvs = useMemo(
    () => unwrapAvs(deliverData?.encounterAvs) || readAvsText(currentCase, currentEncounterNumber(currentCase)),
    [deliverData?.encounterAvs, currentCase],
  )

  const handleDownloadAvs = () => {
    import('../../utils/recordsPackage').then(async ({ generateAvsPdf }) => {
      try {
        const { saveOrShareBlob } = await import('../../utils/saveOrShareBlob')
        const encNum = currentEncounterNumber(currentCase)
        const blob = generateAvsPdf({
          text: deliverAvs,
          title: currentCase?.caseNumber ? `Case ${currentCase.caseNumber}` : '',
          // PROD-957: the PDF of the active encounter's own AVS carries that encounter's finalize
          //   date; otherwise the latest signed visit's date from the store.
          dateStr: formatDate((deliverData?.encounterAvs && deliverData?.encounterFinalizedAt) || currentCase?.finalizedAt || currentCase?.updatedAt || new Date(), { month: 'long', day: 'numeric', year: 'numeric' }),
          patientName: getPatientName(profile),
        })
        const suffix = encNum ? `-encounter-${encNum}` : ''
        await saveOrShareBlob(blob, `Orella_After_Visit_Summary${suffix}.pdf`)
      } catch (e) {
        // TEST 2.06 (P2): the screen stays on Deliver on a download failure —
        // no navigation away, no user-facing error surface.
        console.error('[AVS_DOWNLOAD] failed', e?.name || e)
      }
    })
  }

  // Profile data from profile store
  const { profile } = useProfileStore()
  const insurance = profile?.insurance || {}
  const allergies = profile?.allergies || {}
  const medications = profile?.medications || {}
  const hasInsurance = !!(insurance.provider || insurance.selfPay || insurance.completed)
  const insuranceLabel = insurance.selfPay ? t('deliver.selfPay') : (insurance.provider || null)
  const allergyCount = ((allergies.medList || []).length + (allergies.foodList || []).length + (allergies.contactList || []).length)
  const noKnownAllergies = allergies.hasMedAllergies === false && allergies.hasFoodAllergies === false
  const medCount = (medications.list || []).length
  const noMeds = medications.hasMedications === false
  
  // Pharmacy from case
  const pharmacy = currentCase?.preferredPharmacy || currentCase?.preferred_pharmacy || null
  
  // Auto-advance reopen steps (stagger animation)
  useEffect(() => {
    if (reopenMode && reopenStep === 0) {
      const t = setTimeout(() => setReopenStep(1), 300)
      return () => clearTimeout(t)
    }
  }, [reopenMode, reopenStep])
  
  // v121.5.2: Handle reopen — converts N+1 from complete→follow_up IN-PLACE (no new encounter)
  // Visit number stays the same. Only case-level fields change.
  const handleReopenSubmit = async () => {
    if (reopenSending || !currentCase?.id) return
    // TEST 2.12 — a patient reopen needs a real slot (the API refuses one without it with
    //   400 requested_slot_required); say so here rather than send an empty request.
    if (!reopenDate || !toHHMM(reopenTime)) {
      alert(t('appointment.errRequestedSlotRequired'))
      return
    }
    // ★★ P7 A8/P1 (TEST 2.10; Alex Q7) — a failed upload is shown and BLOCKS submit; it is
    //   never sent silently. An upload still in flight blocks too (nothing here waits for it).
    if (reopenPhotos.some((p) => p.uploading)) {
      alert(t('deliver.followUpPhotosPending'))
      return
    }
    if (reopenPhotos.some((p) => p.error)) {
      alert(t('deliver.followUpPhotoFailed'))
      return
    }
    setReopenSending(true)
    try {
      // v121.6.1: Use dedicated reopen endpoint — bypasses protected_statuses guard
      const result = await casesAPI.reopen(currentCase.id, {
        requestedDate: reopenDate,
        // TEST 2.12 — 'HH:MM' (24h). This used to send the button LABEL ('3:45 PM').
        requestedTime: toHHMM(reopenTime),
        visitType: reopenVisitType,
        requestedBy: 'patient',
        // ★ P7 A8/P1 (TEST 2.10; Alex Q6, Q7) — optional; omitted entirely when empty, so an
        //   old server that has never seen these keys gets exactly today's request.
        ...(reopenNotes.trim() ? { notes: reopenNotes.trim() } : {}),
        ...(reopenPhotoKeys.length ? { photoKeys: reopenPhotoKeys } : {}),
      })
      
      // PROD-903: flags and ids only — the answer carries the new appointment row and the follow_up JSONB.
      console.log('[Deliver] Case reopened via dedicated endpoint:', { appointmentCreated: !!result?.appointmentCreated, appointmentId: result?.appointmentId ?? null, newEncounterNumber: result?.newEncounterNumber ?? null })

      // TEST 2.12 — a 2xx that says no appointment was created is NOT a success; the
      //   patient stays here and is told, and the store is refreshed to the server's truth.
      //   Round 4 (L3): the follow-up IS open, so the copy says so and does not invite a
      //   retry (a second reopen is 409 already_reopened).
      if (result?.appointmentCreated === false) {
        alert(t('appointment.errReopenAppointmentNotCreated'))
        try {
          await useCaseStore.getState().loadCases()
        } catch (storeErr) {
          console.warn('[Deliver] Store refresh failed (non-blocking):', storeErr)
        }
        return
      }
      
      // v121.8.1: Optimistically update the store with reopen response data
      // This ensures CaseHome sees the new encounter immediately (no race with loadCases)
      try {
        const newEncNum = result.newEncounterNumber || result.currentEncounter || currentEncounterNumber(currentCase) + 1
        updateAndSaveCase({
          status: 'in_follow_up',
          encounterPhase: 'deliver_active',
          currentEncounter: newEncNum,
          // ★★★ PROD-192 — NO appointmentRequest HERE. This PUT used to carry
          //   result.appointmentRequest || { status: 'pending', reopened: true } — an
          //   invented fallback. The reopen route WRITES appointment_request itself;
          //   the client only mirrors it (below).
          followUp: result.followUp || result.follow_up || { ...currentCase.followUp, reopened: true, appointmentStatus: 'pending', type: 'in_follow_up' },
        })
        console.log('[Deliver] Store optimistically updated: currentEncounter=', newEncNum)
      } catch (optErr) {
        console.warn('[Deliver] Optimistic update failed (non-blocking):', optErr)
      }

      // PROD-192 — LOCAL mirror only, as for the observation bridge above.
      try {
        const serverRequest = await readServerAppointmentRequest(currentCase.id)
        if (serverRequest || result?.appointmentRequest) updateCase({ appointmentRequest: serverRequest || result.appointmentRequest })
      } catch (readErr) {
        console.warn('[Deliver] appointment_request read-back failed (non-fatal):', readErr?.message || readErr)
        if (result?.appointmentRequest) updateCase({ appointmentRequest: result.appointmentRequest })
      }
      
      // Also trigger a full refresh in background to sync any server-side data
      try {
        const { useCaseStore } = await import('../../stores/index')
        await useCaseStore.getState().loadCases()
        console.log('[Deliver] Store refreshed after reopen')
      } catch (storeErr) {
        console.warn('[Deliver] Store refresh failed (non-blocking):', storeErr)
      }
      
      navigate('/case/home')
    } catch (err) {
      console.error('[Deliver] Reopen submit failed:', err)
      // ★★ Round 4 (L3) — 500 appointment_not_created: the case IS reopened on the server
      //   and only the appointments row failed. The shared errAppointmentNotCreated says
      //   "please try again", and a retry is now 409 already_reopened; this copy is the
      //   API's own ("Your follow-up was opened, but the appointment could not be saved").
      const reopenedWithoutAppointment = err?.code === 'appointment_not_created'
      // doctor_has_no_hours / requested_slot_required / slot_conflict / already_reopened
      // each have their own sentence; anything else keeps the generic one
      alert(t(reopenedWithoutAppointment
        ? 'appointment.errReopenAppointmentNotCreated'
        : (appointmentErrorKey(err) || 'errors2.failedToSendFollowUp')))
      // P1 round 3 (H3) — 409 already_reopened: the case is already open for follow-up
      //   on the server (a reopen from another device, or a retry). Nothing was written;
      //   read the case again so this screen stops offering the reopen it came from.
      //   Round 4 (L3): the same after appointment_not_created, where the reopen DID happen.
      if (err?.code === 'already_reopened' || reopenedWithoutAppointment) {
        try { await useCaseStore.getState().loadCases() } catch (e) { /* non-blocking */ }
      }
    } finally {
      setReopenSending(false)
    }
  }
  
  // v240.0.0 (REF-AK): handleStartReferralCase removed — patients no longer spawn
  // referral cases (the leaky POST /cases/<id>/refer path is retired). Orella referrals
  // are accepted by the recipient MD; external referrals are auto-spawned as a
  // share-coded intake draft by the gated POST /referrals flow.

  // v240.0.2 (REF-O): open the referral letter (already drafted onto this case by the
  // referring MD) in a print/save view. No fetch — content comes from case data.
  const handleViewReferralLetter = (referralRec) => {
    try {
      const ok = openReferralLetter({ currentCase, profile, referral: referralRec })
      if (!ok) console.warn('[Deliver] Referral letter could not be opened')
    } catch (e) {
      console.error('[Deliver] Referral letter render failed:', e)
    }
  }

  // ==========================================
  // SUBMIT PRE-VISIT REPORT
  // ==========================================
  const handleSubmit = async () => {
    if (!currentCase?.id || submitting) return
    setSubmitting(true)
    // v225.23.0 (Bug 3): see modal path — guard against false-fail + reset phase.
    let reportSaved = false
    try {
      await runOverlaySequence(setSubmitPhase, async () => {
        // v173.0.0: Build report data — unified flow includes categorized answers
        const reportData = {
          voiceTranscript: voiceMemo?.transcript || '',
          voiceAudioUrl: voiceMemo?.audioUrl || null,
          // v173.0.0: When unified flow is active, symptomSurvey and medicationReview 
          // are auto-extracted from unified answers by category
          symptomSurvey: useUnifiedFlow ? {} : (symptomSurvey || {}),
          medicationReview: useUnifiedFlow ? {} : (medicationReview || {}),
          careOrders: {
            items: deliverData?.careOrders || [],
            // ★ TEST 2.14 — see the syncPreVisitToEncounter builder above.
            checkedItems: legacyIds('careOrders', deliverData?.careOrders || [], deliver.careTicks, `e${encounterNumber}`, dayKey()),
            complete: deliver.careOrdersComplete || false,
            savedAt: deliver.careOrdersSavedAt || null,
          },
        }

        // v173.0.0: Add unified follow-up questions data
        if (useUnifiedFlow && unifiedQuestions) {
          const categorized = categorizeFollowUpAnswers(unifiedQuestions, unifiedAnswers, unifiedDetails)
          reportData.followUpQuestions = {
            questions: unifiedQuestions,
            answers: unifiedAnswers,
            details: unifiedDetails,
            categorized: categorized,
            source: 'ai-unified',
            generatedAt: new Date().toISOString(),
          }
          // Also populate symptomSurvey/medicationReview from categorized data for backward compatibility
          if (Object.keys(categorized.symptom).length > 0) {
            reportData.symptomSurvey = { ...categorized.symptom, _source: 'ai-unified' }
          }
          if (Object.keys(categorized.medication).length > 0 || Object.keys(categorized.side_effect).length > 0) {
            reportData.medicationReview = { ...categorized.medication, ...categorized.side_effect, _source: 'ai-unified' }
          }
        }
        
        const result = await encountersAPI.submitPreVisitReport(currentCase.id, encounterNumber, reportData)
        reportSaved = true
        console.log('[Deliver] Pre-visit report submitted:', !!result?.success) // PROD-903: the answer echoes the report
        
        // v173.0.0: Persist unified answers to deliver data (non-fatal — the
        // report already reached the doctor at this point).
        try {
          if (useUnifiedFlow) {
            await updateAndSaveCase({ 
              encounterPhase: 'diagnose_pending', 
              deliverState: 'submitted',
              deliver: { ...deliver, unifiedAnswersData: unifiedAnswers, unifiedDetailsData: unifiedDetails }
            })
          } else {
            await updateAndSaveCase({ encounterPhase: 'diagnose_pending', deliverState: 'submitted' })
          }
        } catch (saveErr) {
          console.warn('[Deliver] post-submit case save failed (non-fatal):', saveErr)
        }
        setSubmitted(true)
      }, () => setSubmitPhase(null))
      // v217.1.6: No longer navigate on overlay completion — the submitted
      // view renders in place; the user taps "Back to Case" when ready.
    } catch (err) {
      if (reportSaved) {
        console.warn('[Deliver] post-submit sequence error (report WAS saved):', err)
        setSubmitted(true)
      } else {
        console.error('[Deliver] Submit error:', err)
        alert(t('errors2.failedToSubmit'))
      }
    } finally {
      setSubmitting(false)
    }
  }

  const handleTaskClick = (task) => {
    if (task.state) navigate(task.path, { state: task.state })
    else navigate(task.path)
  }

  // FHOOK: the `!currentCase` early return now sits BELOW the last hook of this component
  //   (rules of hooks). Everything above it reads currentCase with ?. only.

  // ==========================================
  // LAST VISIT RECAP (AI-generated for FollowUpReportModal)
  // ==========================================
  const hasAvs = !!(currentCase?.avsContent || currentCase?.diagnose?.avsContent || currentCase?.afterVisitSummary || currentCase?.visitSummary || currentCase?.consultNote)
  const [visitRecap, setVisitRecap] = useState(currentCase?.visit_recap || currentCase?.visitRecap || null)
  const [recapLoading, setRecapLoading] = useState(false)

  useEffect(() => {
    if (!currentCase?.id || !hasAvs || visitRecap) return
    let cancelled = false
    setRecapLoading(true)
    casesAPI.getVisitRecap(currentCase.id).then(result => {
      if (cancelled) return
      if (result?.recap) {
        setVisitRecap(result.recap)
        // Cache locally so we don't re-fetch
        updateAndSaveCase({ visitRecap: result.recap })
      }
    }).catch(err => {
      console.error('[Deliver] Visit recap fetch error:', err)
    }).finally(() => {
      if (!cancelled) setRecapLoading(false)
    })
    return () => { cancelled = true }
  }, [currentCase?.id, hasAvs])

  const lastVisitDate = currentCase?.diagnose?.completedAt ? formatDate(currentCase.diagnose.completedAt, { month: 'long', day: 'numeric' }) : null
  const lastVisitDoctor = doctorData ? clinicianName(withCaseClinician(doctorData, currentCase)) : null

  // ==========================================
  // SUBMITTED STATE
  // ==========================================
  // v175.0.0: Skip this view when submitPhase overlay is active — overlay handles the transition
  const alreadySubmitted = submitted || currentCase?.encounterPhase === 'deliver_submitted' || currentCase?.encounterPhase === 'diagnose_pending'

  // v217.1.6: When the success view becomes visible (after submit overlay
  // completes), scroll the page to top so the user sees the confirmation,
  // and auto-redirect to case home after a brief delay so they aren't
  // stuck on the view. Manual "Back to Case" still works at any time.
  // v217.1.6 / v225.23.0 (Bug 3): Show the success confirmation once the report
  // is submitted and the overlay is no longer covering the screen (phase null or
  // sliding out via 'exit'). Previously this gated on `!submitPhase`, but the
  // overlay sequence leaves submitPhase at 'exit' on success, so the view never
  // appeared in place — producing the transient/at-the-bottom rendering. No
  // auto-redirect: the user taps "Back to Case" when ready (the old 4s
  // setTimeout auto-forwarded off this screen while the button also showed).
  const showSubmittedView = alreadySubmitted && submitPhase !== 'processing' && submitPhase !== 'success'
  useEffect(() => {
    if (currentCase && showSubmittedView) window.scrollTo(0, 0)
  }, [showSubmittedView])

  // DELETE-CURRENTCASE-NULL-1 — DECLARATIVE REDIRECT, NOT navigate() IN RENDER.
  // Calling navigate() here updates the router WHILE this component is rendering, and
  // React logs exactly that: "Cannot update a component (BrowserRouter) while rendering
  // a different component". That is the React error a patient saw when they deleted a
  // case mid-describe — deleteCase() nulls currentCase, this screen re-rendered on the
  // null and took this arm. The delete and the redirect both completed, which is why it
  // read as cosmetic. <Navigate> is the supported way to say the same thing.
  if (!currentCase) return <Navigate to="/home" replace />
  if (showSubmittedView) {
    return (
      <div className="min-h-full bg-[#F8FAFC] flex flex-col">
        <ChatKeyframes />
        <CompactHeader stepNum={4} totalSteps={4} stepLabel="Deliver" caseInfo={caseInfo} statusLabel="Submitted" onBack={() => caseBack(navigate)} />
        {/* v217.1.6: Use default brand-blue Orella bubble (was green). */}
        <OrellaChatBubble message={t('deliver.responsesSentMsg')}
          animationKey={`deliver-submitted-${currentCase?.id}`} />
        <div className="flex-1 px-4 pt-4 flex flex-col items-center justify-center">
          <div className="w-16 h-16 rounded-full flex items-center justify-center mb-4" style={{ background: GREEN_LIGHT }}>
            <CheckCircle size={32} style={{ color: GREEN_DARK }} />
          </div>
          <h3 className="text-[16px] font-bold text-gray-800 mb-1">{t('deliver.submittedSuccess')}</h3>
          <p className="text-[13px] text-gray-400 text-center px-8">{t('deliver.preVisitSent')}</p>
        </div>
        <div className="p-4 pb-5 bg-white border-t border-gray-100">
          <button onClick={() => navigate('/case/home')} className="w-full py-3 rounded-full text-[14px] font-bold border-none cursor-pointer" style={{ background: `linear-gradient(135deg, ${NAVY}, #1E4A6E)`, color: '#fff' }}>{t('deliver.backToCase')}</button>
        </div>
      </div>
    )
  }

  // ==========================================
  // v121.4.0: CLOSED CASE STATE — Care Complete
  // No pre-visit tasks, no submit — just discharge summary + reopen
  // ==========================================
  const isClosedCase = deliverState === 'complete' || currentCase?.encounterPhase === 'complete'
  if (isClosedCase) {
    // ★★★ TEST 1.35 / TEST 2.08 — ONLY what /slots returned. The fallbacks that stood here
    //   (the next 14 weekdays, and a static 8:00 AM–4:30 PM list) offered a physician with
    //   no hours times they never opened. Times stay 'HH:MM', sorted BY MINUTES —
    //   formatting first and sorting the labels put 1:00 PM before 9:00 AM — and
    //   AppointmentCalendar formats them for display.
    const reopenDateOptions = datesOf(reopenSlots)
    const reopenTimeSlots = timesForDate(reopenSlots, reopenDate)
    const timeSlots = reopenTimeSlots
    
    return (
      <div className="min-h-full bg-[#F8FAFC] flex flex-col">
        <ChatKeyframes />
        <CompactHeader stepNum={4} totalSteps={4} stepLabel="Deliver" caseInfo={caseInfo}
          statusLabel={reopenMode ? t('deliver.followUpRequest') : 'Complete'}
          onBack={() => { if (reopenMode) { setReopenMode(false); setReopenStep(0) } else caseBack(navigate) }}
          onNotification={() => navigate('/notifications')} />

        <div className="flex-1 overflow-auto pb-20">
          {/* Orella chat bubble */}
          <OrellaChatBubble 
            message={reopenMode 
              ? t('deliver.letsGetFollowUp')
              : t('deliver.caseCompleteDeliverMsg')}
            animationKey={`deliver-complete-${currentCase?.id}-${reopenMode ? 'reopen' : 'closed'}`} 
          />
          <div className="h-2" />

          {/* Doctor mini card */}
          {doctorData && (
            <AnimatedCard visible={true} delay={100}>
              <DoctorMiniCard doctor={doctorData} onViewProfile={() => setShowDoctorProfile(doctorData)} />
            </AnimatedCard>
          )}

          {/* ═══ CLOSED STATE: Care Complete Card ═══ */}
          {!reopenMode && (
            <>
              <AnimatedCard visible={true} delay={300}>
                <CareCompleteCard 
                  isPCP={followUpPlan === 'pcp'}
                  signOutNotes={deliverData?.signOutNotes}
                  reasonsToReturn={deliverData?.reasonsToReturn}
                  avsContent={deliverAvs}
                  onDownloadAvs={deliverAvs ? handleDownloadAvs : undefined}
                  onRequestFollowUp={() => { setReopenMode(true); setReopenStep(0) }}
                  isReopening={false}
                />
              </AnimatedCard>
              {(deliverData?.encounterReferrals?.length > 0) && (
                <AnimatedCard visible={true} delay={500}>
                  {deliverData.encounterReferrals.map((ref, idx) => (
                    <ReferralCard key={ref.id || idx} 
                      referralData={{ specialty: ref.specialty, notes: ref.notes || ref.clinical_reason }}
                      followUp={{}} referralRecord={ref} caseId={currentCase?.id} onViewReferralLetter={handleViewReferralLetter}
                       />
                  ))}
                </AnimatedCard>
              )}
            </>
          )}

          {/* ═══ REOPEN BRIDGE: Staggered Dialogue Flow ═══ */}
          {reopenMode && (
            <>
              {/* ─── Step 1: Schedule ─── */}
              <SmallOrellaBubble visible={reopenStep >= 1} delay={200}
                message={t('deliver.whenSeeDoctor')} />
              <AnimatedCard visible={reopenStep >= 1} delay={500}>
                <div className="rounded-[16px] overflow-hidden border-2 bg-white mb-3" style={{ borderColor: LIGHT_BLUE }}>
                  <div className="h-[3px]" style={{ backgroundColor: BLUE }} />
                  <div className="p-4">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: ICE_BLUE }}>
                        <Calendar size={20} style={{ color: BLUE }} />
                      </div>
                      <div>
                        <h3 className="font-bold text-[15px]" style={{ color: NAVY }}>{t('deliver.scheduleFollowUp')}</h3>
                        <p className="text-[11px] text-gray-400">{t('deliver.pickDateTime')}</p>
                      </div>
                    </div>
                    {reopenSlotsLoading ? (
                      <div className="mb-3 py-6 flex justify-center">
                        <OrellaSpinner size={18} tone="inherit" className="text-gray-300" />
                      </div>
                    ) : reopenSlotsReason ? (
                      <NoSlotsCard reason={reopenSlotsReason}
                        phone={doctorData?.phone || doctorData?.practice_phone}
                        onRetry={() => setReopenSlotsAttempt((n) => n + 1)} />
                    ) : (
                    <div className="mb-3">
                      <AppointmentCalendar
                        availableDates={reopenDateOptions}
                        timeSlots={reopenDate ? timeSlots : []}
                        selectedDate={reopenDate}
                        selectedTime={reopenTime}
                        onSelectDate={(d) => { setReopenDate(d); setReopenTime('') }}
                        onSelectTime={setReopenTime}
                        timeSubLabels={reopenDate ? slotPlaceNames(reopenPlaces, reopenDate, timeSlots, reopenVisitType === 'telehealth') : null}
                      />
                    </div>
                    )}
                    <div className="mb-3">
                      <label className="text-[11px] font-bold text-gray-500 uppercase tracking-wider block mb-1.5">{t('deliver.visitType')}</label>
                      <div className="flex gap-2">
                        <button onClick={() => setReopenVisitType('in-person')}
                          className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl border-2 text-[12px] font-semibold transition-all"
                          style={{ borderColor: reopenVisitType === 'in-person' ? BLUE : '#E2E8F0', backgroundColor: reopenVisitType === 'in-person' ? ICE_BLUE : 'white', color: reopenVisitType === 'in-person' ? NAVY : '#94A3B8' }}>
                          <Building size={14} /> {t('caseHome.inPerson')}
                        </button>
                        <button onClick={() => setReopenVisitType('telehealth')}
                          className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl border-2 text-[12px] font-semibold transition-all"
                          style={{ borderColor: reopenVisitType === 'telehealth' ? BLUE : '#E2E8F0', backgroundColor: reopenVisitType === 'telehealth' ? ICE_BLUE : 'white', color: reopenVisitType === 'telehealth' ? NAVY : '#94A3B8' }}>
                          <Video size={14} /> {t('appointment.telehealth', 'Telehealth')}
                        </button>
                      </div>
                    </div>
                    <SlotPlaceLine place={reopenDate && reopenTime ? slotPlace(reopenPlaces, reopenDate, reopenTime, reopenVisitType === 'telehealth') : null} className="mb-3 px-1" />
                    {reopenDate && reopenTime && (
                      <button onClick={() => setReopenStep(2)}
                        className="w-full py-2.5 rounded-full text-white text-[13px] font-bold border-none cursor-pointer"
                        style={{ backgroundColor: BLUE }}>
                        {t('common.continue', 'Continue')}
                      </button>
                    )}
                  </div>
                </div>
              </AnimatedCard>

              {/* ─── Step 2: Pharmacy ─── */}
              <SmallOrellaBubble visible={reopenStep >= 2} delay={200}
                message={t('deliver.letsConfirmPharmacy')} />
              <AnimatedCard visible={reopenStep >= 2} delay={500}>
                <div className="rounded-[16px] overflow-hidden border-2 border-gray-200 bg-white mb-3">
                  <div className="h-[3px]" style={{ backgroundColor: NAVY }} />
                  <div className="p-4">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-xl bg-slate-50 flex items-center justify-center">
                        <Building size={20} style={{ color: NAVY }} />
                      </div>
                      <div>
                        <h3 className="font-bold text-[15px]" style={{ color: NAVY }}>{t('deliver.preferredPharmacy')}</h3>
                        <p className="text-[11px] text-gray-400">{t('deliver.forPrescriptions')}</p>
                      </div>
                    </div>
                    {pharmacy ? (
                      <div className="bg-slate-50 rounded-xl p-3 mb-3 border border-slate-100">
                        <p className="text-[13px] font-semibold text-gray-800">{pharmacy.name || pharmacy.pharmacyName || 'Your Pharmacy'}</p>
                        {(pharmacy.address || pharmacy.pharmacyAddress) && <p className="text-[11px] text-gray-500 mt-0.5">{pharmacy.address || pharmacy.pharmacyAddress}</p>}
                        {(pharmacy.phone || pharmacy.pharmacyPhone) && (
                          <div className="flex items-center gap-1 mt-1"><Phone size={10} className="text-gray-400" /><span className="text-[11px] text-gray-500">{pharmacy.phone || pharmacy.pharmacyPhone}</span></div>
                        )}
                      </div>
                    ) : (
                      <div className="bg-amber-50 rounded-xl p-3 mb-3 border border-amber-100">
                        <p className="text-[12px] text-amber-700">{t('deliver.noPharmacy')}</p>
                      </div>
                    )}
                    <button onClick={() => setReopenStep(3)}
                      className="w-full py-2.5 rounded-full text-white text-[13px] font-bold border-none cursor-pointer"
                      style={{ backgroundColor: NAVY }}>
                      {pharmacy ? t('deliver.confirmPharmacy') : t('deliver.continueWithoutPharmacy')}
                    </button>
                  </div>
                </div>
              </AnimatedCard>

              {/* ─── Step 3: Insurance ─── */}
              <SmallOrellaBubble visible={reopenStep >= 3} delay={200}
                message={t('deliver.confirmInsurance')} />
              <AnimatedCard visible={reopenStep >= 3} delay={500}>
                <div className="rounded-[16px] overflow-hidden border-2 border-gray-200 bg-white mb-3">
                  <div className="h-[3px]" style={{ backgroundColor: NAVY }} />
                  <div className="p-4">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-xl bg-slate-50 flex items-center justify-center">
                        <FileText size={20} style={{ color: NAVY }} />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-bold text-[15px]" style={{ color: NAVY }}>{t('deliver.insuranceInfo')}</h3>
                        <p className="text-[11px] text-gray-400">{t('deliver.confirmCoverage')}</p>
                      </div>
                      {hasInsurance && <CheckCircle size={18} className="text-green-500" />}
                    </div>
                    {hasInsurance ? (
                      <div className="bg-slate-50 rounded-xl p-3 mb-3 border border-slate-100">
                        <p className="text-[13px] font-semibold text-gray-800">{insuranceLabel}</p>
                        {insurance.memberId && <p className="text-[11px] text-gray-500 mt-0.5">{t('deliver.memberIdLabel', 'Member ID:')} {insurance.memberId}</p>}
                        {insurance.groupNumber && <p className="text-[11px] text-gray-500">{t('deliver.groupLabel', 'Group:')} {insurance.groupNumber}</p>}
                      </div>
                    ) : (
                      <div className="bg-amber-50 rounded-xl p-3 mb-3 border border-amber-100">
                        <p className="text-[12px] text-amber-700">{t('deliver.noInsurance')}</p>
                      </div>
                    )}
                    <div className="flex gap-2">
                      <button onClick={() => navigate('/profile/insurance', { state: { returnTo: location.pathname, returnState: { reopen: true, reopenDraft: reopenDraftFor() } } })}
                        className="flex-1 py-2.5 rounded-xl text-[12px] font-semibold border border-slate-200 bg-white text-slate-600 active:bg-slate-50">
                        {hasInsurance ? t('deliver.changeInsurance') : t('deliver.addInsurance')}
                      </button>
                      <button onClick={() => setReopenStep(4)}
                        className="flex-1 py-2.5 rounded-full text-white text-[13px] font-bold border-none cursor-pointer"
                        style={{ backgroundColor: NAVY }}>
                        {hasInsurance ? 'Confirm' : t('deliver.skipForNow')}
                      </button>
                    </div>
                  </div>
                </div>
              </AnimatedCard>

              {/* ─── Step 4: Allergies & Medications ─── */}
              <SmallOrellaBubble visible={reopenStep >= 4} delay={200}
                message={t('deliver.anyNewAllergies')} />
              <AnimatedCard visible={reopenStep >= 4} delay={500}>
                <div className="rounded-[16px] overflow-hidden border-2 border-gray-200 bg-white mb-3">
                  <div className="h-[3px]" style={{ backgroundColor: NAVY }} />
                  <div className="p-4 space-y-3">
                    {/* Allergies */}
                    <div className="flex items-center justify-between bg-slate-50 rounded-xl px-3 py-3 border border-slate-100">
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-lg bg-red-50 flex items-center justify-center">
                          <AlertCircle size={16} className="text-red-500" />
                        </div>
                        <div>
                          <p className="text-[13px] font-semibold text-gray-800">{t('deliver.allergies')}</p>
                          <p className="text-[11px] text-gray-400">
                            {noKnownAllergies ? t('deliver.noKnownAllergies') 
                              : allergyCount > 0 ? `${allergyCount} allerg${allergyCount === 1 ? 'y' : 'ies'} on file`
                              : t('deliver.notReviewed')}
                          </p>
                        </div>
                      </div>
                      <button onClick={() => navigate('/profile/allergies', { state: { returnTo: location.pathname, returnState: { reopen: true, reopenDraft: reopenDraftFor() } } })}
                        className="text-[11px] font-semibold px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-slate-500 active:bg-slate-50">
                        {allergyCount > 0 || noKnownAllergies ? 'Update' : 'Add'}
                      </button>
                    </div>

                    {/* Medications */}
                    <div className="flex items-center justify-between bg-slate-50 rounded-xl px-3 py-3 border border-slate-100">
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
                          <Pill size={16} className="text-blue-500" />
                        </div>
                        <div>
                          <p className="text-[13px] font-semibold text-gray-800">{t('deliver.medications')}</p>
                          <p className="text-[11px] text-gray-400">
                            {noMeds ? t('deliver.noCurrentMedications')
                              : medCount > 0 ? `${medCount} medication${medCount === 1 ? '' : 's'} on file`
                              : t('deliver.notReviewed')}
                          </p>
                        </div>
                      </div>
                      <button onClick={() => navigate('/profile/medications', { state: { returnTo: location.pathname, returnState: { reopen: true, reopenDraft: reopenDraftFor() } } })}
                        className="text-[11px] font-semibold px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-slate-500 active:bg-slate-50">
                        {medCount > 0 || noMeds ? 'Update' : 'Add'}
                      </button>
                    </div>

                    <button onClick={() => setReopenStep(5)}
                      className="w-full py-2.5 rounded-full text-white text-[13px] font-bold border-none cursor-pointer"
                      style={{ backgroundColor: NAVY }}>
                      {t('deliver.confirmAndContinue', 'Confirm & Continue')}
                    </button>
                  </div>
                </div>
              </AnimatedCard>

              {/* ─── Step 5: Send to Doctor ─── */}
              <SmallOrellaBubble visible={reopenStep >= 5} delay={200}
                message={t('deliver.allSetSendFollowUp')} />

              {/* ★★ P7 A8/P1 (TEST 2.10; Alex Q6, Q7) — optional text and up to 5 photos on
                  the follow-up request. Photos upload as soon as they are picked, not on
                  submit (Q7); a failed one is shown and blocks Send. */}
              <AnimatedCard visible={reopenStep >= 5} delay={350}>
                <div className="rounded-[16px] overflow-hidden border-2 border-gray-200 bg-white mb-3">
                  <div className="h-[3px]" style={{ backgroundColor: NAVY }} />
                  <div className="p-4">
                    <label className="text-[11px] font-bold text-gray-500 uppercase tracking-wider block mb-1.5">
                      {t('deliver.followUpNotesLabel')}
                    </label>
                    <textarea
                      value={reopenNotes}
                      onChange={(e) => setReopenNotes(e.target.value.slice(0, 1000))}
                      maxLength={1000}
                      rows={3}
                      placeholder={t('deliver.followUpNotesPlaceholder')}
                      className="w-full rounded-xl border border-slate-200 p-3 text-[13px] text-gray-700 resize-none focus:outline-none focus:border-blue-300"
                    />
                    <p className="text-[10.5px] text-gray-400 text-right mt-1">{reopenNotes.length}/1000</p>

                    <label className="text-[11px] font-bold text-gray-500 uppercase tracking-wider block mt-3 mb-1.5">
                      {t('deliver.followUpPhotosLabel')}
                    </label>
                    {reopenPhotos.length > 0 && (
                      <div className="flex flex-col gap-1.5 mb-2">
                        {reopenPhotos.map((p) => (
                          <div key={p.id} className="flex items-center gap-2 bg-slate-50 rounded-lg px-2.5 py-2 border border-slate-100">
                            {p.uploading ? <OrellaSpinner size={16} tone="inherit" className="flex-shrink-0 text-gray-400" />
                              : p.error ? <AlertCircle size={14} className="text-red-500 flex-shrink-0" />
                              : <CheckCircle size={14} className="text-green-500 flex-shrink-0" />}
                            <span className="text-[12px] text-gray-600 truncate flex-1">{p.name}</span>
                            {p.error && <span className="text-[10.5px] text-red-500 flex-shrink-0">{t('deliver.followUpPhotoFailed')}</span>}
                            <button type="button" onClick={() => removeReopenPhoto(p.id)} aria-label={t('common.remove')} className="flex-shrink-0 text-gray-400">
                              <X size={14} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    {reopenPhotos.filter((p) => !p.error).length < 5 && (
                      <label className="flex items-center justify-center gap-2 py-2.5 rounded-xl border border-dashed border-slate-300 text-[12px] font-semibold text-slate-500 cursor-pointer active:bg-slate-50">
                        <Camera size={16} />
                        {t('detect.addPhotos')}
                        <input type="file" accept="image/*" multiple className="hidden" onChange={handleReopenPhotoPick} />
                      </label>
                    )}
                  </div>
                </div>
              </AnimatedCard>

              <AnimatedCard visible={reopenStep >= 5} delay={500}>
                <button onClick={handleReopenSubmit} disabled={reopenSending || reopenPhotos.some((p) => p.uploading || p.error)}
                  className="w-full py-4 rounded-[14px] border-none text-[15px] font-bold flex items-center justify-center gap-2 transition-all cursor-pointer disabled:opacity-50"
                  style={{ background: `linear-gradient(135deg, ${NAVY}, #1E4A6E)`, color: '#fff', boxShadow: '0 4px 16px rgba(21,53,79,0.2)' }}>
                  {reopenSending 
                    ? <><OrellaSpinner size={18} tone="inherit" /> {t('deliver.sendingRequest')}</>
                    : <><Send size={18} /> {t('deliver.sendFollowUpRequestToDoctor')}</>
                  }
                </button>
              </AnimatedCard>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 pb-5 flex-shrink-0 bg-white border-t border-gray-100">
          <button onClick={() => { if (reopenMode) { setReopenMode(false); setReopenStep(0) } else navigate('/case/home') }}
            className="w-full py-3 rounded-[14px] text-[13px] font-semibold cursor-pointer border-[1.5px] border-gray-200 bg-white text-[#15354F] flex items-center justify-center gap-2 hover:bg-gray-50 transition-colors">
            {reopenMode ? '← Back to Case Summary' : 'Case Home'}
          </button>
        </div>
        <DoctorProfileHost doctor={showDoctorProfile} onClose={() => setShowDoctorProfile(null)} />
      </div>
    )
  }

  // ==========================================
  // MAIN DIALOGUE FLOW
  // ==========================================
  const chatTint = getChatTint()

  return (
    <div className="min-h-full bg-[#F8FAFC] flex flex-col">
      <ChatKeyframes />

      <CompactHeader stepNum={4} totalSteps={4} stepLabel="Deliver" caseInfo={caseInfo}
        statusLabel={deliverState === 'complete' ? 'Complete' : deliverState === 'all-done' ? 'Ready' : deliverState === 'observation' ? t('deliver.observation') : 'Active'}
        onBack={() => caseBack(navigate)} onNotification={() => navigate('/notifications')} />

      <div className="flex-1 overflow-auto pb-20">

        {/* Step 0: Greeting */}
        <OrellaChatBubble message={getChatMessage()} animationKey={`deliver-${currentCase?.id}-${deliverState}`}
          onComplete={() => setStep(1)} customStyle={chatTint}
          chips={[{
            label: hasPlanToWorkThrough ? t('deliver.askOrellaAboutPlan') : t('agent.askOrella'),
            onClick: () => openAgent?.(null, 'deliver'),
          }]} />


        {/* Step 1: Doctor card */}
        {doctorData && (
          <AnimatedCard visible={step >= 1} delay={100}>
            <DoctorMiniCard doctor={doctorData} onViewProfile={() => setShowDoctorProfile(doctorData)} />
          </AnimatedCard>
        )}

        {/* Step 2: State-specific intro bubble (skip for follow-up — header dialogue covers it) */}
        {deliverState !== 'follow-up' && (
          <SmallOrellaBubble visible={step >= 2} delay={100} message={getStateIntro()} />
        )}

        {deliverState === 'follow-up' && !(currentCase?.doctorSource === 'external' || currentCase?.doctor_source === 'external') && (
          <AnimatedCard visible={step >= 2} delay={300}>
            {/* ★★★ v242.121.0 · FU-SCHEDULE-INLINE-1 — the card opens INTO the scheduler,
                on this page, the way the initial appointment is scheduled inside
                ShareConversation. The component is the real ScheduleFollowupScreen in
                embedded mode, so there is exactly one booking path. */}
            {schedulingInline ? (
              <ScheduleFollowupScreen
                embedded
                returnToProp="/case/deliver"
                onScheduledProp={() => setSchedulingInline(false)}
              />
            ) : (
              <FollowUpScheduleCard timing={deliverData?.followUpTiming} visitType={deliverData?.visitType}
                onSchedule={() => setSchedulingInline(true)} />
            )}
          </AnimatedCard>
        )}

        {deliverState === 'follow-up' && (currentCase?.doctorSource === 'external' || currentCase?.doctor_source === 'external') && (
          <AnimatedCard visible={step >= 2} delay={300}>
            <div className="rounded-[16px] overflow-hidden border-2 border-slate-200 bg-white mb-3">
              <div className="h-[3px] bg-slate-300" />
              <div className="p-4 text-center">
                <div className="w-11 h-11 rounded-xl bg-slate-100 flex items-center justify-center mx-auto mb-2"><Calendar size={22} className="text-slate-400" /></div>
                <h3 className="font-bold text-[14px]" style={{ color: NAVY }}>{t('deliver.followUpScheduling')}</h3>
                <p className="text-[12px] text-slate-400 mt-1 leading-relaxed">{t('deliver.doctorManagesFollowUp')}</p>
              </div>
            </div>
          </AnimatedCard>
        )}

        {hasAppointment && (deliverState === 'scheduled' || deliverState === 'all-done') && (
          <AnimatedCard visible={step >= 2} delay={300}>
            {schedulingInline ? (
              <ScheduleFollowupScreen
                embedded
                returnToProp="/case/deliver"
                onScheduledProp={() => setSchedulingInline(false)}
              />
            ) : (
              <AppointmentSummaryCard appointment={appointment} visitType={deliverData?.visitType}
                onReschedule={() => setSchedulingInline(true)}
                // s126c: Join goes through the check-in, then the video sign-on, then the visit
                onJoinCheckIn={() => navigate(joinCheckInPath(currentCase?.id, appointment?.join?.id))} />
            )}
          </AnimatedCard>
        )}

        {deliverState === 'observation' && (
          <>
            {/* Observation card — stagger in after intro bubble */}
            <AnimatedCard visible={step >= 2} delay={300}>
              <ObservationCard notes={deliverData?.observationNotes || signedFollowUp.observationNotes}
                timing={deliverData?.followUpTiming} followUp={signedFollowUp}
                deliverData={deliverData} />
            </AnimatedCard>
          </>
        )}

        {deliverState === 'complete' && (
          <AnimatedCard visible={step >= 2} delay={300}>
            <CareCompleteCard isPCP={followUpPlan === 'pcp'}
              signOutNotes={deliverData?.signOutNotes}
              reasonsToReturn={deliverData?.reasonsToReturn}
              avsContent={deliverAvs}
              onDownloadAvs={deliverAvs ? handleDownloadAvs : undefined}
              onRequestFollowUp={() => navigate('/case/schedule-followup', { state: { patientRequested: true, returnTo: '/case/deliver' } })} />
          </AnimatedCard>
        )}

        {deliverState === 'referral' && (
          <AnimatedCard visible={step >= 2} delay={300}>
            <ReferralCard referralData={{ specialty: deliverData?.referralSpecialty, notes: deliverData?.referralNotes }} followUp={signedFollowUp}
              referralRecord={deliverData?.encounterReferrals?.[0]}
              caseId={currentCase?.id}
              onViewReferralLetter={handleViewReferralLetter}
               />
          </AnimatedCard>
        )}
        
        {/* v120.9.5 PA-C4: Encounter referral task (from referrals table, shown alongside any state) */}
        {(deliverData?.encounterReferrals?.length > 0) && deliverState !== 'referral' && (
          <AnimatedCard visible={step >= 2} delay={400}>
            {deliverData.encounterReferrals.map((ref, idx) => (
              <ReferralCard key={ref.id || idx} 
                referralData={{ specialty: ref.specialty, notes: ref.notes || ref.clinical_reason }}
                followUp={{}}
                referralRecord={ref}
                caseId={currentCase?.id}
                onViewReferralLetter={handleViewReferralLetter}
                 />
            ))}
          </AnimatedCard>
        )}

        {/* Order-based task cards (care instructions, imaging, etc.) — after appointment */}
        {orderTasks.map((task, i) => (
          <AnimatedCard key={task.id} visible={step >= 2} delay={500 + (i * 150)}>
            <CareCard
              icon={task.icon}
              title={task.title}
              subtitle={task.items.length > 0
                ? `${task.items.length} item${task.items.length === 1 ? '' : 's'}`
                : task.description}
              items={task.items} checkedIds={task.checkedIds}
              pill={task.status === 'scheduled' ? 'scheduled' : task.pill}
              pillLabel={task.status === 'completed' ? 'Done' : task.status === 'scheduled' ? 'Scheduled' : task.pillLabel}
              status={task.status} facility={task.facility} onClick={() => handleTaskClick(task)} />
          </AnimatedCard>
        ))}

        {/* v174.0.1: Follow-up cases — Follow-Up Report card (after order tasks) */}
        {/* v174.5.0: Removed Orella dialogue — just the card */}
        {isFollowUpCase && deliverState !== 'observation' && (
        <>
        <AnimatedCard visible={step >= 3} delay={200}>
          <CareCard icon={Mic}
            title={t('deliver.followupReport')}
            subtitle={useUnifiedFlow 
              ? `Report complete · ${Object.keys(unifiedAnswers).length} answers`
              : voiceMemo ? `${voiceMemo.transcript ? t('deliver.noteSaved') : t('deliver.recordingSaved')}` 
              : 'Record a voice note or type an update'}
            status={useUnifiedFlow ? 'complete' : voiceMemo ? 'complete' : 'pending'}
            pill="required" pillLabel={useUnifiedFlow || voiceMemo ? 'Sent' : 'Required'}
            meta={useUnifiedFlow || voiceMemo ? undefined : ts('deliver.doctorReadsBefore', 'Your doctor reads this before your visit')}
            onClick={() => setActiveModal('voice')} />
          {/* s128: a confirmed scale is information on the plan, not a task with a due chip */}
          {!(useUnifiedFlow || voiceMemo) && followUpScales.map(({ def }) => (
            <p key={def.scale} data-scale-plan-info={def.scale} className="text-[12px] text-gray-500 mt-1.5 px-1">
              {t('deliver.scalePlanInfo', { scale: def.scale })}
            </p>
          ))}
        </AnimatedCard>
        </>
        )}

        {/* Step 2 continued: Progress bar — hidden during observation and follow-up cases */}
        {/* v174.0.1: Follow-up cases skip progress bar — just show Follow-Up Report card */}
        {/* v130.0.2: Also hidden until appointment is scheduled — checklist unlocks after scheduling */}
        {deliverState !== 'observation' && !!appointment && !isFollowUpCase && (
        <>
        <AnimatedCard visible={step >= 2} delay={700 + (orderTasks.length * 150)}>
          <div className="bg-white rounded-2xl border border-[#15354F] p-3.5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[13px] font-bold text-gray-800">
                {deliverState === 'referral' ? t('deliver.beforeReferral') : t('deliver.beforeNextVisit')}
              </span>
              <span className="text-[12px] font-semibold" style={{ color: BLUE }}>{completedTasks}/{totalTasks} done</span>
            </div>
            <div className="h-[6px] bg-slate-100 rounded-full overflow-hidden">
              <div className="h-full rounded-full transition-all duration-500" style={{ width: `${totalTasks > 0 ? (completedTasks / totalTasks) * 100 : 0}%`, backgroundColor: BLUE }} />
            </div>
            {allTasksComplete && (
              <div className="mt-2 flex items-center gap-1.5 text-[12px] font-semibold bg-green-50 text-green-700 rounded-lg p-2">
                <Check size={14} strokeWidth={2.5} /> {t('deliver.allTasksComplete', 'All tasks complete — ready for your visit!')}
              </div>
            )}
          </div>
        </AnimatedCard>

        {/* Step 3: Follow-Up Report (voice) — GATED */}
        <SmallOrellaBubble visible={step >= 3} delay={100}
          message={t('deliver.hearHowYouBeen')} />
        <AnimatedCard visible={step >= 3} delay={400}>
          <CareCard icon={Mic}
            title={t('deliver.followupReport')}
            subtitle={useUnifiedFlow 
              ? `Report complete · ${Object.keys(unifiedAnswers).length} answers`
              : voiceMemo ? `${voiceMemo.transcript ? t('deliver.noteSaved') : t('deliver.recordingSaved')}` 
              : 'Record a voice note or type an update'}
            status={useUnifiedFlow ? 'complete' : voiceMemo ? 'complete' : 'pending'}
            pill="required" pillLabel={useUnifiedFlow || voiceMemo ? 'Sent' : 'Required'}
            meta={useUnifiedFlow || voiceMemo ? undefined : ts('deliver.doctorReadsBefore', 'Your doctor reads this before your visit')}
            onClick={() => setActiveModal('voice')} />
          {!voiceMemo && !useUnifiedFlow && step === 3 && (
            <button onClick={handleSkipVoice}
              className="w-full text-center text-[12px] font-semibold text-gray-400 py-1.5 -mt-1 mb-1 active:text-gray-500 bg-transparent border-none cursor-pointer">
              {t('deliver.skipForNow', 'Skip for now')}
            </button>
          )}
        </AnimatedCard>

        {/* ══════ STEP 4+: POST-VOICE FLOW (v173.0.1) ══════ */}
        {/* For follow-up cases: the FollowUpReportModal handles voice + questions internally */}
        {/* DeliverScreen only shows the Follow-Up Report card (step 3) and Submit (step 6) */}
        {/* For non-follow-up (initial encounter): legacy separate modals remain */}
        
        {showUnifiedPath ? (
          <>
            {/* Step 6: Submit — visible after full report submitted from modal */}
            {useUnifiedFlow && (
              <AnimatedCard visible={step >= 6} delay={300}>
                <SmallOrellaBubble visible={step >= 6} delay={100}
                  message={ts('deliver.allSetSubmit', "You're all set. Now submit your responses to your doctor for review.")} />
                <button onClick={handleSubmit} disabled={!canSubmit || submitting}
                  className={`w-full py-4 rounded-[14px] border-none text-[15px] font-bold flex items-center justify-center gap-2 transition-all cursor-pointer ${canSubmit && !submitting ? 'breathe-navy' : ''}`}
                  style={{
                    background: canSubmit ? `linear-gradient(135deg, ${NAVY}, #1E4A6E)` : '#E2E8F0',
                    color: canSubmit ? '#fff' : '#94A3B8',
                    boxShadow: canSubmit ? '0 4px 16px rgba(21,53,79,0.2)' : 'none',
                  }}>
                  {submitting ? <><OrellaSpinner size={18} tone="inherit" /> {t('deliver.submitting')}</> : <><Send size={18} /> {t('deliver.submitResponsesToDoctor')}</>}
                </button>
              </AnimatedCard>
            )}
          </>
        ) : (
          <>
            {/* FALLBACK: Legacy separate modals (Step 4: Symptom Check-In — GATED) */}
            <SmallOrellaBubble visible={step >= 4} delay={100}
              message={t('deliver.quickCheckIn')} />
            <AnimatedCard visible={step >= 4} delay={400}>
              <CareCard icon={Heart}
                title={t('deliver.symptomCheckin')}
                subtitle={symptomSurvey ? `${Object.keys(symptomSurvey).length} responses saved` : 'Rate your current symptoms'}
                status={symptomSurvey ? 'complete' : 'pending'}
                pill="required" pillLabel={symptomSurvey ? 'Done' : 'Required'}
                onClick={() => !symptomSurvey && setActiveModal('symptoms')} />
              {!symptomSurvey && step === 4 && (
                <button onClick={handleSkipSymptom}
                  className="w-full text-center text-[12px] font-semibold text-gray-400 py-1.5 -mt-1 mb-1 active:text-gray-500 bg-transparent border-none cursor-pointer">
                  {t('deliver.skipForNow', 'Skip for now')}
                </button>
              )}
            </AnimatedCard>

            {/* FALLBACK: Step 5: Medication Review (if meds) — GATED */}
            {hasMeds && (
              <SmallOrellaBubble visible={step >= 5} delay={100}
                message={t('deliver.reviewMedications')} />
            )}
            {hasMeds && (
              <AnimatedCard visible={step >= 5} delay={400}>
                <CareCard icon={Pill}
                  title={t('deliver.medicationReview')}
                  subtitle={medicationReview ? `${Object.keys(medicationReview).length} responses saved` : deliverData.medications.slice(0, 2).map(m => typeof m === 'string' ? m : asText(m?.name) || 'Medication').join(', ')}
                  status={medicationReview ? 'complete' : 'pending'}
                  pill="neutral" pillLabel={medicationReview ? 'Done' : 'Pending'}
                  onClick={() => !medicationReview && setActiveModal('medications')} />
                {!medicationReview && step === 5 && (
                  <button onClick={handleSkipMed}
                    className="w-full text-center text-[12px] font-semibold text-gray-400 py-1.5 -mt-1 mb-1 active:text-gray-500 bg-transparent border-none cursor-pointer">
                    {t('deliver.skipForNow', 'Skip for now')}
                  </button>
                )}
              </AnimatedCard>
            )}

            {/* FALLBACK: Step 6: Submit */}
            <AnimatedCard visible={step >= 6} delay={300}>
              <div className="flex items-start gap-2.5 mb-3">
                <img src={orellaOIcon} alt="O" className="w-9 h-9 rounded-lg flex-shrink-0 object-contain" />
                <div className={`flex-1 ${DIALOGUE_BUBBLE} ${DIALOGUE_PAD}`}>
                  <p className="text-[15.5px] font-medium leading-relaxed" style={{ color: NAVY }}>
                    {allTasksComplete
                      ? t('deliver.allSetSubmit')
                      : t('deliver.submitResponses')}
                  </p>
                </div>
              </div>
              <button onClick={handleSubmit} disabled={!canSubmit || submitting}
                className={`w-full py-4 rounded-[14px] border-none text-[15px] font-bold flex items-center justify-center gap-2 transition-all cursor-pointer ${canSubmit && !submitting ? 'breathe-navy' : ''}`}
                style={{
                  background: canSubmit ? `linear-gradient(135deg, ${NAVY}, #1E4A6E)` : '#E2E8F0',
                  color: canSubmit ? '#fff' : '#94A3B8',
                  boxShadow: canSubmit ? '0 4px 16px rgba(21,53,79,0.2)' : 'none',
                }}>
                {submitting ? <><OrellaSpinner size={18} tone="inherit" /> {t('deliver.submitting')}</> : <><Send size={18} /> {t('deliver.submitResponsesToDoctor')}</>}
              </button>
              {!canSubmit && <p className="text-[11px] text-gray-400 text-center mt-2">{t('deliver.completeVoiceNote')}</p>}
            </AnimatedCard>
          </>
        )}
        </>
        )}

        {/* v124.5.0: Check-In Card (next due) + Report a Change button */}
        {deliverState === 'observation' && !checkInMode && !reportMode && (
          <>
            {/* v135.3.0 PA-C2: Check-up history log — shows completed check-ups from encounter pre_visit_report */}
            {(() => {
              const obsEnc = (currentCase?.encounters || []).find(e => e.category === 'observation')
              const pvr = obsEnc?.pre_visit_report || {}
              // D332(j): read the new key, fall back to the legacy one for any row
              // written before the rename. The server folds it forward on first write.
              const checkIns = Array.isArray(pvr.checkUps) ? pvr.checkUps
                : (Array.isArray(pvr.checkIns) ? pvr.checkIns : [])
              if (checkIns.length === 0) return null
              return (
                <AnimatedCard visible={step >= 3} delay={200}>
                  <div className="rounded-[14px] border-[1.5px] border-gray-200 bg-white mb-3 overflow-hidden">
                    <div className="px-3.5 py-2.5 border-b border-gray-100 flex items-center gap-2">
                      <ListChecks size={14} style={{ color: NAVY }} />
                      <span className="text-[12px] font-bold" style={{ color: NAVY }}>{t('deliver.pastCheckUps', { count: checkIns.length })}</span>
                    </div>
                    <div className="divide-y divide-gray-50">
                      {checkIns.slice(-5).reverse().map((ci, i) => (
                        <div key={i} className="px-3.5 py-2 flex items-center gap-2.5">
                          <CheckCircle size={14} className="text-green-500 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-[12px] font-semibold text-gray-700">
                              {t('deliver.checkUpNumber', { number: checkIns.length - i })}
                            </p>
                            <p className="text-[10px] text-gray-400">
                              {ci.date ? (formatCalendarDay(ci.date, { month: 'short', day: 'numeric' }, 'en-US') || '—') : '—'}
                              {' · '}{ci.reportedChange ? t('deliver.checkUpReportedChange') : t('deliver.checkUpNoChanges')}
                            </p>
                          </div>
                          {ci.reportedChange && (
                            <span className="text-[9px] font-bold px-2 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200">{t('deliver.change')}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </AnimatedCard>
              )
            })()}

            {/* Step 4: Check-up card — staggers after care instructions */}
            <AnimatedCard visible={step >= 4} delay={200}>
              <CheckUpCard
                loading={obsCheckInLoading}
                number={(() => {
                  const pvr = currentCase?.encounters?.find(e => 
                    (e.category === 'observation') && e.pre_visit_report
                  )?.pre_visit_report || {}
                  const checkIns = pvr.checkUps || pvr.checkIns || []
                  return checkIns.length + 1
                })()}
                dueDate={(() => {
                  // ★★★ D332(m) — THE SERVER OWNS THE SCHEDULE, AND THIS READS IT.
                  //   Everything below the first line is a FALLBACK for rows written before
                  //   `nextCheckUpDue` existed. It used to be the only implementation, and
                  //   the reminder cron would have needed the same arithmetic in Python —
                  //   two implementations of one rule, which is how the app and the
                  //   reminder come to disagree about when a patient is due and nothing
                  //   errors. `compute_next_check_up_due` is the one owner now; it stamps
                  //   the date at finalize and advances it whenever an answer lands.
                  const stored = deliverData?.nextCheckUpDue
                  if (stored) return formatDate(stored, { month: 'short', day: 'numeric' })
                  const freq = deliverData?.checkInSchedule || signedFollowUp.checkInFrequency || 'weekly'
                  const freqDays = { daily: 1, every_3_days: 3, weekly: 7, biweekly: 14, monthly: 30 }
                  const days = freqDays[freq] || 7
                  const pvr = currentCase?.encounters?.find(e => e.category === 'observation')?.pre_visit_report || {}
                  const lastCheckIn = (pvr.checkUps || pvr.checkIns || []).slice(-1)[0]
                  const baseDate = lastCheckIn?.date ? new Date(lastCheckIn.date) : new Date(deliverData?.observationStartDate || Date.now())
                  const due = new Date(baseDate.getTime() + days * 86400000)
                  return formatDate(due.toISOString(), { month: 'short', day: 'numeric' })
                })()}
                onTap={() => {
                  handleObsCheckInTap()
                }}
              />
            </AnimatedCard>

            {/* Step 5: Report a Change — red, final element */}
            <AnimatedCard visible={step >= 5} delay={200}>
              <button onClick={() => { setReportMode(true); setReportStep(0) }}
                className="w-full py-2.5 border-2 border-red-200 bg-red-50 text-red-700 rounded-full text-[13px] font-bold flex items-center justify-center gap-2 active:bg-red-100 cursor-pointer">
                <AlertCircle size={15} /> {t('deliver.reportAChange', 'Report a Change')}
              </button>
            </AnimatedCard>
          </>
        )}

        {/* ═══ v125.0.0: CHECK-IN CHAT THREAD FLOW ═══ */}
        {deliverState === 'observation' && checkInMode && !reportMode && (
          <>
            {/* Q1: Orella asks */}
            <SmallOrellaBubble visible={checkInStep >= 1} delay={200}
              message={t('deliver.noticedSigns')} />
            
            {/* Q1: Choice buttons (before selection) OR Patient bubble (after selection) */}
            <AnimatedCard visible={checkInStep >= 1} delay={500}>
              {checkInQ1 === null ? (
                /* Choice buttons — right-aligned */
                <div className="flex justify-end gap-2 px-4 mb-2">
                  <button onClick={() => { setCheckInQ1(false); setTimeout(() => setCheckInStep(2), 600) }}
                    className="px-4 py-2.5 rounded-xl text-[12.5px] font-semibold border-none cursor-pointer"
                    style={{ backgroundColor: NAVY, color: 'white', boxShadow: '0 2px 8px rgba(21,53,79,0.2)' }}>
                    {t('deliver.nothingToReport')}
                  </button>
                  <button onClick={() => { setCheckInQ1(true); setTimeout(() => { setCheckInMode(false); setReportMode(true); setReportStep(0) }, 600) }}
                    className="px-4 py-2.5 rounded-xl text-[12.5px] font-semibold border-none cursor-pointer"
                    style={{ backgroundColor: NAVY, color: 'white', boxShadow: '0 2px 8px rgba(21,53,79,0.2)' }}>
                    {t('deliver.iHaveNewSymptoms')}
                  </button>
                </div>
              ) : (
                /* Patient response bubble — right-aligned with Change button */
                <div className="flex justify-end items-center gap-2 px-4 mb-2">
                  <button onClick={() => { setCheckInQ1(null); setCheckInQ2(null); setCheckInStep(1) }}
                    className="flex items-center gap-1 px-2 py-1 rounded-lg bg-transparent border border-gray-200 cursor-pointer"
                    style={{ flexShrink: 0 }}>
                    <ChevronRight size={12} className="text-gray-400 rotate-180" />
                    <span className="text-[10px] text-gray-400 font-semibold">{t('deliver.change')}</span>
                  </button>
                  <div className="rounded-tl-[14px] rounded-tr-[2px] rounded-br-[14px] rounded-bl-[14px] px-3.5 py-2.5"
                    style={{ backgroundColor: NAVY, maxWidth: '70%' }}>
                    <p className="text-[12.5px] font-semibold text-white m-0 leading-snug">
                      {checkInQ1 === false ? t('deliver.nothingToReport') : t('deliver.iHaveNewSymptoms')}
                    </p>
                  </div>
                </div>
              )}
            </AnimatedCard>

            {/* Q2: Only if Q1 = nothing to report */}
            {checkInQ1 === false && (
              <>
                <SmallOrellaBubble visible={checkInStep >= 2} delay={200}
                  message={t('deliver.noticedConcerning')} />
                <AnimatedCard visible={checkInStep >= 2} delay={500}>
                  {checkInQ2 === null ? (
                    <div className="flex justify-end gap-2 px-4 mb-2">
                      <button onClick={() => { setCheckInQ2(false); handleCheckInSubmit() }}
                        className="px-4 py-2.5 rounded-xl text-[12.5px] font-semibold border-none cursor-pointer"
                        style={{ backgroundColor: NAVY, color: 'white', boxShadow: '0 2px 8px rgba(21,53,79,0.2)' }}>
                        {t('deliver.nothingToReport')}
                      </button>
                      <button onClick={() => { setCheckInQ2(true); setTimeout(() => { setCheckInMode(false); setReportMode(true); setReportStep(0) }, 600) }}
                        className="px-4 py-2.5 rounded-xl text-[12.5px] font-semibold border-none cursor-pointer"
                        style={{ backgroundColor: NAVY, color: 'white', boxShadow: '0 2px 8px rgba(21,53,79,0.2)' }}>
                        {t('deliver.iHaveNewSymptoms')}
                      </button>
                    </div>
                  ) : (
                    <div className="flex justify-end items-center gap-2 px-4 mb-2">
                      <button onClick={() => { setCheckInQ2(null); setCheckInStep(2) }}
                        className="flex items-center gap-1 px-2 py-1 rounded-lg bg-transparent border border-gray-200 cursor-pointer"
                        style={{ flexShrink: 0 }}>
                        <ChevronRight size={12} className="text-gray-400 rotate-180" />
                        <span className="text-[10px] text-gray-400 font-semibold">{t('deliver.change')}</span>
                      </button>
                      <div className="rounded-tl-[14px] rounded-tr-[2px] rounded-br-[14px] rounded-bl-[14px] px-3.5 py-2.5"
                        style={{ backgroundColor: NAVY, maxWidth: '70%' }}>
                        <p className="text-[12.5px] font-semibold text-white m-0 leading-snug">
                          {checkInQ2 === false ? t('deliver.nothingToReport') : t('deliver.iHaveNewSymptoms')}
                        </p>
                      </div>
                    </div>
                  )}
                </AnimatedCard>
              </>
            )}

            {/* Complete — no findings */}
            {checkInStep >= 3 && (
              <>
                <SmallOrellaBubble visible={true} delay={200}
                  message={t('deliver.checkUpComplete')} />
                <AnimatedCard visible={true} delay={500}>
                  <div className="rounded-[14px] border-[1.5px] border-green-200 bg-green-50 p-3.5 mb-2 flex items-center gap-3">
                    <CheckCircle size={18} className="text-green-500 flex-shrink-0" />
                    <div>
                      <p className="text-[12.5px] font-bold text-green-800">{t('deliver.checkinSubmitted')}</p>
                      <p className="text-[10.5px] text-green-600">
                        {new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' , timeZone: 'America/Los_Angeles'})} · Next check-in in 1 week
                      </p>
                    </div>
                  </div>
                  <button onClick={() => navigate('/case/home')}
                    className="w-full py-2.5 text-[13px] font-bold bg-transparent border-none cursor-pointer"
                    style={{ color: NAVY }}>
                    {t('caseHome.backToCaseHome', 'Back to Case Home')}
                  </button>
                </AnimatedCard>
              </>
            )}
          </>
        )}

        {/* ═══ v124.0.0: REPORT FINDING BRIDGE FLOW ═══ */}
        {deliverState === 'observation' && reportMode && (
          <>
            {/* Step 1: Voice memo */}
            <SmallOrellaBubble visible={reportStep >= 1} delay={200}
              message={t('deliver.tellUsHowFeeling')} />
            <AnimatedCard visible={reportStep >= 1} delay={500}>
              <div className="rounded-[16px] overflow-hidden border-2 bg-white mb-2" style={{ borderColor: LIGHT_BLUE }}>
                <div className="h-[3px]" style={{ backgroundColor: BLUE }} />
                <div className="p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: ICE_BLUE }}>
                      <Mic size={20} style={{ color: BLUE }} />
                    </div>
                    <div>
                      <h3 className="font-bold text-[15px]" style={{ color: NAVY }}>{t('deliver.voiceReport')}</h3>
                      <p className="text-[11px] text-gray-400">{t('deliver.recordExperiencing')}</p>
                    </div>
                  </div>
                  {!reportVoiceMemo ? (
                    <button
                      onClick={() => setActiveModal('voice')}
                      className="w-full py-3.5 rounded-xl border-2 border-dashed text-[13px] font-semibold flex items-center justify-center gap-2 cursor-pointer"
                      style={{ borderColor: BLUE, backgroundColor: ICE_BLUE, color: NAVY }}>
                      <Mic size={18} style={{ color: BLUE }} /> {t('deliver.tapToRecordVoiceMemo', 'Tap to Record Voice Memo')}
                    </button>
                  ) : (
                    <div className="bg-green-50 border border-green-200 rounded-xl p-3 flex items-center gap-2">
                      <CheckCircle size={16} className="text-green-500" />
                      <p className="text-[12px] font-semibold text-green-700 flex-1">{t('deliver.recordingSaved')}</p>
                      <button onClick={() => setReportStep(2)}
                        className="px-4 py-1.5 rounded-lg text-[12px] font-semibold text-white cursor-pointer border-none"
                        style={{ backgroundColor: BLUE }}>
                        {t('common.continue', 'Continue')}
                      </button>
                    </div>
                  )}
                  {reportTranscript && (
                    <p className="text-[11px] text-gray-400 mt-2 line-clamp-2">{reportTranscript}</p>
                  )}
                </div>
              </div>
            </AnimatedCard>

            {/* Step 2: Schedule */}
            <SmallOrellaBubble visible={reportStep >= 2} delay={200}
              message={t('deliver.letsConnectDoctor')} />
            <AnimatedCard visible={reportStep >= 2} delay={500}>
              <div className="rounded-[16px] overflow-hidden border-2 bg-white mb-2" style={{ borderColor: LIGHT_BLUE }}>
                <div className="h-[3px]" style={{ backgroundColor: BLUE }} />
                <div className="p-4">
                  {/* Doctor info */}
                  {doctorData && (
                    <div className="flex items-center gap-2.5 mb-3 p-2.5 rounded-xl bg-gray-50 border border-gray-100">
                      <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: ICE_BLUE }}>
                        <span className="text-[13px] font-bold" style={{ color: NAVY }}>
                          {(doctorData.first_name?.[0] || '').toUpperCase()}{(doctorData.last_name?.[0] || '').toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <p className="text-[13px] font-bold" style={{ color: NAVY }}>
                          Dr. {doctorData.first_name} {doctorData.last_name}
                        </p>
                        <p className="text-[11px] text-gray-400">{doctorData.specialty || currentCase?.specialtyName || t('deliver.specialist')}</p>
                      </div>
                    </div>
                  )}
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: ICE_BLUE }}>
                      <Calendar size={20} style={{ color: BLUE }} />
                    </div>
                    <div>
                      <h3 className="font-bold text-[15px]" style={{ color: NAVY }}>{t('deliver.scheduleFollowUp')}</h3>
                      <p className="text-[11px] text-gray-400">{t('deliver.pickDateTime')}</p>
                    </div>
                  </div>
                  {reportSlotsLoading ? (
                    <div className="mb-3 py-6 flex justify-center">
                      <OrellaSpinner size={18} tone="inherit" className="text-gray-300" />
                    </div>
                  ) : reportSlotsReason ? (
                    <>
                      <NoSlotsCard reason={reportSlotsReason}
                        phone={doctorData?.phone || doctorData?.practice_phone}
                        onRetry={() => setReportSlotsAttempt((n) => n + 1)} />
                      {reasonAllowsSendWithoutSlot(reportSlotsReason) && !reportConcernSent && (
                        <p className="text-[12px] text-gray-500 text-center mb-3">{t('appointment.sendUpdateWithoutTime')}</p>
                      )}
                    </>
                  ) : (
                    <div className="mb-3">
                      <AppointmentCalendar
                        availableDates={datesOf(reportSlots)}
                        timeSlots={reportDate ? timesForDate(reportSlots, reportDate) : []}
                        selectedDate={reportDate}
                        selectedTime={reportTime}
                        onSelectDate={(d) => { setReportDate(d); setReportTime('') }}
                        onSelectTime={setReportTime}
                        timeSubLabels={reportDate ? slotPlaceNames(reportPlaces, reportDate, timesForDate(reportSlots, reportDate), reportVisitType === 'telehealth') : null}
                      />
                    </div>
                  )}
                  {!reasonAllowsSendWithoutSlot(reportSlotsReason) && (
                  <div className="flex gap-2 mb-3">
                    <button onClick={() => setReportVisitType('in-person')}
                      className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl border-2 text-[12px] font-semibold transition-all cursor-pointer"
                      style={{ borderColor: reportVisitType === 'in-person' ? BLUE : '#E2E8F0', backgroundColor: reportVisitType === 'in-person' ? ICE_BLUE : 'white', color: reportVisitType === 'in-person' ? NAVY : '#94A3B8' }}>
                      <Building size={14} /> {t('caseHome.inPerson')}
                    </button>
                    <button onClick={() => setReportVisitType('telehealth')}
                      className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl border-2 text-[12px] font-semibold transition-all cursor-pointer"
                      style={{ borderColor: reportVisitType === 'telehealth' ? BLUE : '#E2E8F0', backgroundColor: reportVisitType === 'telehealth' ? ICE_BLUE : 'white', color: reportVisitType === 'telehealth' ? NAVY : '#94A3B8' }}>
                      <Video size={14} /> {t('appointment.telehealth', 'Telehealth')}
                    </button>
                  </div>
                  )}
                  <SlotPlaceLine place={reportDate && reportTime ? slotPlace(reportPlaces, reportDate, reportTime, reportVisitType === 'telehealth') : null} className="mb-3 px-1" />
                  {!reportConcernSent && ((reportDate && reportTime) || (!reportSlotsLoading && reasonAllowsSendWithoutSlot(reportSlotsReason))) && (
                    <button onClick={() => setReportStep(3)}
                      className="w-full py-2.5 rounded-full text-white text-[13px] font-bold border-none cursor-pointer"
                      style={{ backgroundColor: BLUE }}>
                      {t('common.continue', 'Continue')}
                    </button>
                  )}
                </div>
              </div>
            </AnimatedCard>

            {/* Step 3: Send */}
            {reportStep >= 3 && !reportConcernSent && (
              <AnimatedCard visible={true} delay={300}>
                <button onClick={handleReportFindingSubmit} disabled={reportSending}
                  className="w-full py-3.5 rounded-[14px] text-white text-[14px] font-bold border-none cursor-pointer flex items-center justify-center gap-2 disabled:opacity-50"
                  style={{ background: `linear-gradient(135deg, ${NAVY}, #1e4a6b)` }}>
                  {reportSending
                    ? <><OrellaSpinner size={16} tone="inherit" /> {t('deliver.sending')}</>
                    : <><Send size={16} /> {t('deliver.sendToDoctor')}</>
                  }
                </button>
                <p className="text-[10px] text-gray-400 text-center mt-1.5">{t('deliver.doctorWillReview')}</p>
              </AnimatedCard>
            )}

            {/* D-h (round 3, H1; round 4, L1): the concern arrived; the appointment did not */}
            {reportConcernSent && (
              <AnimatedCard visible={true} delay={100}>
                <div className="bg-green-50 border border-green-200 rounded-xl p-3 flex items-start gap-2" data-report-concern-sent>
                  <CheckCircle size={16} className="text-green-500 flex-shrink-0 mt-0.5" />
                  <p className="text-[12px] font-semibold text-green-700 flex-1">{t(concernSentMessageKey(reportConcernSent))}</p>
                </div>
              </AnimatedCard>
            )}

            {/* Back button for bridge flows */}
            <div className="mt-2">
              <button onClick={() => navigate('/case/home')}
                className="w-full py-2.5 text-[13px] font-bold bg-transparent border-none cursor-pointer"
                style={{ color: NAVY }}>
                {t('caseHome.backToCaseHome', 'Back to Case Home')}
              </button>
            </div>
          </>
        )}
      </div>

      {/* Footer */}
      <div className="p-4 pb-5 flex-shrink-0 bg-white border-t border-gray-100">
        <button onClick={() => navigate('/case/home')} className="w-full text-gray-400 py-2 text-[13px] font-semibold bg-transparent border-none cursor-pointer">{t('deliver.backToCase')}</button>
      </div>

      {/* Doctor Profile Modal */}
      <DoctorProfileHost doctor={showDoctorProfile} onClose={() => setShowDoctorProfile(null)} />

      {/* ========== v120.4 MODALS ========== */}
      <FollowUpReportModal 
        open={activeModal === 'voice'} 
        onClose={() => setActiveModal(null)}
        onSubmit={handleVoiceComplete}
        lastVisitSummary={visitRecap}
        lastVisitDate={lastVisitDate}
        lastVisitDoctor={lastVisitDoctor}
        recapLoading={recapLoading}
        // v173.0.0: Unified follow-up report props
        caseId={currentCase?.id}
        encounterId={currentEncounterId(currentCase)}
        isFollowUp={isFollowUpCase}
        // ★★★ CHECKUP-MODAL-REUSE-1. isFollowUpCase reads eight signals; deliverState reads
        //   ONE (deliverData.followUpPlan). A case they disagree on satisfies the card's gate
        //   and the monitored patient is asked for a voice memo. One owner answers both now.
        includeVoice={!isObservationCase(currentCase, deliverData)}
        bridgeType={bridgeTypeFor(currentCase, deliverData)}
        language={language}
        initialUnifiedQuestions={unifiedQuestions}
        initialUnifiedAnswers={unifiedAnswers}
        initialUnifiedDetails={unifiedDetails}
        scaleItems={isFollowUpCase ? followUpScales : []}
      />
      <SymptomCheckModal 
        open={activeModal === 'symptoms'} 
        onClose={() => setActiveModal(null)}
        onSubmit={handleSymptomComplete}
        questions={symptomQuestions}
        caseLabel={caseInfo}
      />
      <MedicationReviewModal 
        open={activeModal === 'medications'} 
        onClose={() => setActiveModal(null)}
        onSubmit={handleMedComplete}
        medications={deliverData?.medications || []}
        aiQuestions={medAiQuestions}
      />

      {/* v164.0.0: AI Observation Check-Up Modal */}
      <SymptomCheckModal
        open={showObsCheckInModal}
        onClose={() => setShowObsCheckInModal(false)}
        onSubmit={handleObsCheckInComplete}
        questions={obsCheckInQuestions || []}
        caseLabel={t('deliver.observationCheckUp', { caseInfo })}
      />

      <SubmitOverlay
        phase={submitPhase}
        processingTitle={t('deliver.submittingReport')}
        processingSubtitle={t('deliver.preparingForYourVisit')}
        successTitle={t('deliver.reportSubmitted')}
        successSubtitle={t('deliver.yourDoctorWillReviewThis')}
      />
    </div>
  )
}
